import { useState, useEffect, useRef } from "react";
import ReactDOM from "react-dom";
import ReactECharts from "echarts-for-react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import WatchlistEditModal from "../components/WatchlistEditModal";
import WatchlistTickerModal from "../components/WatchlistTickerModal";
import ConceptChart from "../components/charts/ConceptChart";
import ChartComponent from "../components/charts/PriceHistoryChart";

// NOTE on chart-component reuse:
//   ConceptChart and ChartComponent (price history) are imported from
//   components/charts/ and shared with the mobile page (pages/Mobile.jsx).
//   The CAGR lollipops, CAGR comparison line chart, and Historical P/E / P/B
//   line charts in this file are still defined inline (as IIFEs further down).
//   Clean reusable versions exist at:
//     - components/charts/CagrLollipopCard.jsx
//     - components/charts/CagrComparisonChart.jsx
//     - components/charts/RatioTrendChart.jsx
//   These desktop call sites should be migrated to use those shared components
//   in a follow-up — until then, fixes to those visualizations must be applied
//   in both this file and the shared component files.


const LoadingText = ({ steps, interval = 1500 }) => {
  const [index, setIndex] = useState(0);
  useEffect(() => {
    setIndex(0);
    const timer = setInterval(() => {
      setIndex((prev) => Math.min(prev + 1, steps.length - 1));
    }, interval);
    return () => clearInterval(timer);
  }, [steps, interval]);
  return <span className="text-gray-400 text-sm transition-opacity duration-300">{steps[index]}</span>;
};

const TypewriterText = ({ text, speed = 8, className = "" }) => {
  const [displayedText, setDisplayedText] = useState("");
  useEffect(() => {
    setDisplayedText("");
    if (!text) return;
    let i = 0;
    const interval = setInterval(() => {
      setDisplayedText(text.substring(0, i + 1));
      i++;
      if (i >= text.length) clearInterval(interval);
    }, speed);
    return () => clearInterval(interval);
  }, [text, speed]);
  return <p className={className}>{displayedText}</p>;
};

const TypewriterMarkdown = ({ text, speed = 8 }) => {
  const [displayedText, setDisplayedText] = useState("");
  useEffect(() => {
    setDisplayedText("");
    if (!text) return;
    let i = 0;
    const interval = setInterval(() => {
      setDisplayedText(text.substring(0, i + 1));
      i++;
      if (i >= text.length) clearInterval(interval);
    }, speed);
    return () => clearInterval(interval);
  }, [text, speed]);
  return (
    <ReactMarkdown
      remarkPlugins={[remarkGfm]}
      components={{
        h1: ({ children }) => <h3 className="text-green-400 font-semibold text-base mb-2">{children}</h3>,
        h2: ({ children }) => <h3 className="text-green-400 font-semibold text-base mt-4 mb-2">{children}</h3>,
        h3: ({ children }) => <h4 className="text-green-300 font-semibold text-sm mt-3 mb-2">{children}</h4>,
        p: ({ children }) => <p className="mb-3">{children}</p>,
        ul: ({ children }) => <ul className="list-disc list-outside pl-6 mb-3 space-y-1">{children}</ul>,
        ol: ({ children }) => <ol className="list-decimal list-outside pl-6 mb-3 space-y-1">{children}</ol>,
        li: ({ children }) => <li className="mb-1">{children}</li>,
        strong: ({ children }) => <strong className="text-gray-100 font-semibold">{children}</strong>,
      }}
    >
      {displayedText}
    </ReactMarkdown>
  );
};

// Sankey Chart Component
function SankeyChart({ data, yearOrPeriod, isQuarterly, sankeyMapData, onClose }) {
  if (!data || data.length === 0 || !sankeyMapData || sankeyMapData.length === 0) {
    return (
      <div className="fixed inset-0 z-[1100] bg-gray-900 bg-opacity-95 flex items-center justify-center p-4">
        <div className="bg-gray-800 border border-gray-700 rounded-lg w-full max-w-6xl h-[80vh] flex flex-col">
          <div className="flex justify-between items-center p-4 border-b border-gray-700">
            <h2 className="text-xl font-semibold text-gray-100">
              Income Statement Flow - {yearOrPeriod}
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-200 text-2xl font-bold"
            >
              ×
            </button>
          </div>
          <div className="flex-1 p-4 bg-gray-800/50 flex flex-col items-center justify-center text-gray-400">
            <p className="text-lg">No Sankey mapping data available.</p>
            <p className="text-sm mt-2">Please ensure the backend is running and refresh the page.</p>
          </div>
        </div>
      </div>
    );
  }

  // Filter income statement data for the selected period
  const periodData = data.filter(item => {
    let periodKey;
    if (isQuarterly) {
      periodKey = item.fp ? `${item.fy} ${item.fp}` : String(item.fy);
    } else {
      periodKey = String(item.fy);
    }
    return periodKey === String(yearOrPeriod);
  });

  const valueMap = {};
  periodData.forEach(item => {
    // Only map values that exist, ignore 0 or nulls to prevent empty nodes
    if (item.value !== null && item.value !== undefined && !isNaN(item.value) && item.value !== 0) {
      valueMap[item.concept] = item.value;
    }
  });

  // Helper to shorten long labels
  const formatLabel = (name) => {
    if (!name) return name;
    const labelMap = {
      "Income Loss Before Income Taxes Extraordinary Items": "Income (Loss) before Taxes",
      "Net Income Loss": "Net Income",
      "Selling General And Administrative Expenses": "SG&A Expenses",
      "Research And Development Expenses": "R&D Expenses",
      "Operating Income Loss": "Operating Income",
    };
    return labelMap[name] || name;
  };

  // --- Pre-compute missing values from Sankey map relationships ---
  // Group mappings by step_level for ordered processing
  const stepLevels = [...new Set(sankeyMapData.map(m => m.step_level))].sort((a, b) => a - b);

  stepLevels.forEach(level => {
    const mappingsAtLevel = sankeyMapData.filter(m => m.step_level === level);

    // Group by source to compute remainders
    const bySource = {};
    mappingsAtLevel.forEach(m => {
      const src = m.source_concept || '__root__';
      if (!bySource[src]) bySource[src] = [];
      bySource[src].push(m);
    });

    Object.entries(bySource).forEach(([src, mappings]) => {
      if (src === '__root__') return; // skip root sources

      const sourceVal = valueMap[src];
      if (!sourceVal || sourceVal <= 0) return; // source must have a value

      // Find all outflows and remainders from this source
      const outflows = mappings.filter(m => m.flow_type === 'outflow');
      const remainders = mappings.filter(m => m.flow_type === 'remainder');

      // Sum up outflow values that exist
      let totalOutflow = 0;
      outflows.forEach(m => {
        if (valueMap[m.target_concept] && valueMap[m.target_concept] > 0) {
          totalOutflow += valueMap[m.target_concept];
        }
      });

      // If a remainder target is missing, compute it as source - totalOutflow
      remainders.forEach(m => {
        if (!valueMap[m.target_concept] || valueMap[m.target_concept] === 0) {
          const computed = sourceVal - totalOutflow;
          if (computed > 0) {
            valueMap[m.target_concept] = computed;
          }
        }
      });
    });

    // Handle convergence nodes: multiple sources feeding into the same target
    // (e.g., Net Interest Income + Noninterest Revenue → Total Net Revenue)
    const byTarget = {};
    mappingsAtLevel.forEach(m => {
      if (!m.source_concept) return;
      if (!byTarget[m.target_concept]) byTarget[m.target_concept] = [];
      byTarget[m.target_concept].push(m);
    });

    Object.entries(byTarget).forEach(([target, mappings]) => {
      if (mappings.length > 1 && (!valueMap[target] || valueMap[target] === 0)) {
        // Multiple sources feed into this target — sum available source values
        let total = 0;
        let hasAny = false;
        mappings.forEach(m => {
          if (valueMap[m.source_concept] && valueMap[m.source_concept] > 0) {
            total += valueMap[m.source_concept];
            hasAny = true;
          }
        });
        if (hasAny && total > 0) {
          valueMap[target] = total;
        }
      }
    });
  });

  // --- Build nodes and links from the enriched valueMap ---
  // Pre-compute convergence targets (targets with multiple sources feeding in)
  const targetSourceCount = {};
  sankeyMapData.forEach(mapping => {
    if (mapping.source_concept) {
      targetSourceCount[mapping.target_concept] = (targetSourceCount[mapping.target_concept] || 0) + 1;
    }
  });

  const nodesMap = new Set();
  const links = [];

  sankeyMapData.forEach(mapping => {
    const source = mapping.source_concept;
    const target = mapping.target_concept;

    let linkValue = 0;

    if (!source) {
      // Root node (e.g. Revenue, Interest Income)
      linkValue = valueMap[target];
    } else if (valueMap[source] && valueMap[source] > 0 && valueMap[target] && valueMap[target] > 0) {
      // For convergence nodes (multiple sources → one target), use the source value
      // Otherwise use the target value (standard split: one source → multiple targets)
      if (targetSourceCount[target] > 1) {
        linkValue = valueMap[source];
      } else {
        linkValue = valueMap[target];
      }
    }

    if (linkValue && linkValue > 0) {
      const displaySource = formatLabel(source);
      const displayTarget = formatLabel(target);

      if (source) nodesMap.add(displaySource);
      nodesMap.add(displayTarget);

      if (source) {
        links.push({
          source: displaySource,
          target: displayTarget,
          value: linkValue
        });
      }
    }
  });

  const greenNodes = new Set([
    // Standard company concepts
    "Revenue",
    "Gross Profit",
    "Operating Income",
    "Income (Loss) before Taxes",
    "Net Income",
    // Bank/financial company concepts
    "Interest Income",
    "Net Interest Income",
    "Noninterest Revenue",
    "Total Net Revenue",
    "Pre-tax Income",
    // Insurance company concepts
    "Premiums Earned",
    "Total Revenue"
  ]);

  // Sort nodes so green ones appear first to stay on top
  const nodes = Array.from(nodesMap).map(name => {
    const isGreen = greenNodes.has(name);
    return {
      name,
      itemStyle: {
        color: isGreen ? '#10b981' : '#f87171', // Tailwind's emerald-500 and red-400
        borderWidth: 1,
        borderColor: '#333'
      },
      // Store a tier for sorting: 0 is green, 1 is red
      _sortTier: isGreen ? 0 : 1
    };
  }).sort((a, b) => {
    // Primary sort: Green nodes (0) before Red nodes (1)
    if (a._sortTier !== b._sortTier) {
      return a._sortTier - b._sortTier;
    }
    // Secondary sort: Keep consistent alphabetical or default ordering
    return a.name.localeCompare(b.name);
  });

  const option = {
    backgroundColor: "transparent",
    tooltip: {
      trigger: 'item',
      triggerOn: 'mousemove',
      formatter: function (params) {
        if (!params.value || isNaN(params.value)) return params.name;
        const valM = Math.round(params.value / 1000000);
        if (params.dataType === 'node') {
          return `${params.name}: $${valM}M`;
        } else if (params.dataType === 'edge') {
          return `${params.data.source} → ${params.data.target}<br/>Value: $${valM}M`;
        }
      }
    },
    series: [
      {
        type: 'sankey',
        data: nodes,
        links: links,
        emphasis: {
          focus: 'adjacency'
        },
        nodeAlign: 'left',
        layoutIterations: 0, // Disable automatic layout so our manual sorting order is respected
        lineStyle: {
          color: 'source',
          curveness: 0.5,
          opacity: 0.4
        },
        label: {
          color: '#e5e7eb',
          fontSize: 12,
          formatter: function (params) {
            if (!params.value || isNaN(params.value)) return params.name;
            const valM = Math.round(params.value / 1000000);
            return `${params.name}\n$${valM}M`;
          }
        }
      }
    ]
  };

  return (
    <div className="fixed inset-0 z-[1100] bg-gray-900 bg-opacity-95 flex items-center justify-center p-4">
      <div className="bg-gray-800 border border-gray-700 rounded-lg w-full max-w-6xl h-[80vh] flex flex-col">
        <div className="flex justify-between items-center p-4 border-b border-gray-700">
          <h2 className="text-xl font-semibold text-gray-100">
            Income Statement Flow - {yearOrPeriod}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-200 text-2xl font-bold"
          >
            ×
          </button>
        </div>
        <div className="flex-1 p-4 bg-gray-800/50">
          {nodes.length > 0 && links.length > 0 ? (
            <ReactECharts
              option={option}
              style={{ height: "100%", width: "100%" }}
            />
          ) : (
            <div className="text-center py-20 text-gray-400">
              <p>Insufficient data to render Sankey chart for {yearOrPeriod}</p>
            </div>
          )}
        </div>
        <p className="text-xs text-gray-500 px-4 pb-2">Experimental. Use as visual aid</p>
      </div>
    </div>
  );
}

// Income Statement Table Component
function IncomeStatementTable({ data, isQuarterly, isMaximized = false, onConceptClick, onColumnClick, isCashFlow = false, headerTitle = "Breakdown", onAsReportedClick = null, enableHint = false }) {
  // Show hint only when the table scrolls into view — only for income statement
  const [showHint, setShowHint] = useState(false);
  const tableRef = useRef(null);
  useEffect(() => {
    if (!enableHint) return;
    const el = tableRef.current;
    if (!el) return;
    let timer;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          observer.disconnect();
          setShowHint(true);
          timer = setTimeout(() => setShowHint(false), 3500);
        }
      },
      { threshold: 0.1 }
    );
    observer.observe(el);
    return () => { observer.disconnect(); clearTimeout(timer); };
  }, [enableHint]);

  if (!data || data.length === 0) {
    return (
      <div className="text-center py-8 text-gray-400">
        <p>No income statement data available</p>
      </div>
    );
  }

  // Transform data into rows/columns format
  const transformedData = {};
  const columnsSet = new Set();
  const conceptMeta = {}; // Store metadata (adsh, display_order, plabel) for each concept

  data.forEach((item) => {
    const concept = item.concept;
    const plabel = item.plabel || concept; // Use plabel for display, fallback to concept (tag)
    const fy = item.fy;
    const fp = item.fp;
    const value = item.value;
    const uom = item.uom;
    const adsh = item.adsh;
    const display_order = item.display_order;
    const line = item.line;

    // Create column key based on quarterly or annual
    let columnKey;
    if (isQuarterly) {
      // For quarterly: "2022 Q1", "2022 Q2", etc.
      columnKey = fp ? `${fy} ${fp}` : fy;
    } else {
      // For annual: just the fiscal year "2021", "2022", etc.
      columnKey = fy;
    }

    columnsSet.add(columnKey);

    if (!transformedData[concept]) {
      transformedData[concept] = {};
    }

    // Store value, uom, concept and plabel for formatting
    transformedData[concept][columnKey] = {
      value: value,
      uom: uom,
      concept: concept,
      plabel: plabel,
    };

    // Store metadata for sorting - use the first occurrence's adsh and display_order/line
    if (!conceptMeta[concept]) {
      const sortKey = display_order !== null && display_order !== undefined
        ? display_order
        : (line !== null && line !== undefined ? line : 999999);
      conceptMeta[concept] = {
        adsh: adsh || '',
        sortKey: sortKey,
        orderIndex: Object.keys(conceptMeta).length,
        plabel: plabel
      };
    }
  });

  // Sort columns (years first, then by period for quarterly)
  const columns = Array.from(columnsSet).sort((a, b) => {
    if (isQuarterly) {
      // Extract year and period for quarterly sorting
      const [yearA, periodA] = a.split(' ');
      const [yearB, periodB] = b.split(' ');
      if (yearA !== yearB) {
        return parseInt(yearB) - parseInt(yearA); // Descending year
      }
      // Same year, sort by period descending (Q4 -> Q1)
      const periodOrder = { 'Q1': 1, 'Q2': 2, 'Q3': 3, 'Q4': 4 };
      return (periodOrder[periodB] || 0) - (periodOrder[periodA] || 0);
    } else {
      // Annual: sort by year descending
      return parseInt(b) - parseInt(a);
    }
  });

  // Get unique concepts (rows) and sort by adsh, then display_order/line
  // Preserve the order from the query (already sorted by adsh, display_order/line)
  const concepts = Object.keys(transformedData).sort((a, b) => {
    const metaA = conceptMeta[a];
    const metaB = conceptMeta[b];

    // Sort by sortKey (display_order or line)
    const sortKeyCompare = (metaA.sortKey || 999999) - (metaB.sortKey || 999999);
    if (sortKeyCompare !== 0) {
      return sortKeyCompare;
    }

    // If sortKey is the same, preserve original order
    return (metaA.orderIndex || 0) - (metaB.orderIndex || 0);
  });

  // Calculate Net Cash Flow row for cashflow statements
  const netCashFlowRow = {};
  if (isCashFlow) {
    columns.forEach((col) => {
      let sum = 0;
      concepts.forEach((concept) => {
        const cellData = transformedData[concept][col];
        if (cellData && cellData.value !== null && cellData.value !== undefined && !isNaN(cellData.value)) {
          sum += cellData.value;
        }
      });
      netCashFlowRow[col] = sum;
    });
  }

  // Format value for display
  const formatValue = (cellData, concept) => {
    // Handle both old format (direct value) and new format (object with value and uom)
    let value;
    if (typeof cellData === 'object' && cellData !== null && 'value' in cellData) {
      value = cellData.value;
      // Use concept from cellData if available, otherwise use passed concept
      concept = cellData.concept || concept;
    } else {
      // Fallback for old format (backwards compatibility)
      value = cellData;
    }

    if (value === null || value === undefined || isNaN(value)) {
      return { text: '-', isNegative: false };
    }

    // Check if concept tag OR display label contains "Per Share" / share count keywords
    const conceptLower = (concept || '').toLowerCase();
    const plabelLower = (cellData?.plabel || '').toLowerCase();
    const isPerShare = conceptLower.includes('per share') || plabelLower.includes('per share')
      || conceptLower.includes('pershare');  // handles EarningsPerShareBasic etc.
    const isShareCount = conceptLower.includes('shares outstanding') || conceptLower.includes('shares issued')
      || plabelLower.includes('shares outstanding') || plabelLower.includes('shares issued')
      || (conceptLower.includes('average') && conceptLower.includes('share'));

    const isNegative = value < 0;
    const absValue = Math.abs(value);
    let formattedText;

    if (isShareCount) {
      // For share counts, show as plain number (in millions)
      const millionsValue = absValue / 1000000;
      formattedText = `${millionsValue.toLocaleString('en-US', { maximumFractionDigits: 1 })}M`;
    } else if (!isPerShare) {
      // Standard financial values: divide by 1 million and add $ prefix and M suffix
      const millionsValue = absValue / 1000000;
      formattedText = `$${millionsValue.toLocaleString('en-US', { maximumFractionDigits: 2 })}M`;
    } else {
      // For earnings per share, format normally
      if (absValue >= 1000) {
        formattedText = absValue.toLocaleString('en-US', { maximumFractionDigits: 0 });
      } else {
        formattedText = absValue.toLocaleString('en-US', { maximumFractionDigits: 2 });
      }
    }

    // Wrap negative values in brackets
    if (isNegative) {
      formattedText = `(${formattedText})`;
    }

    return { text: formattedText, isNegative };
  };


  return (
    <>
      {showHint && (
        <style>{`
          @keyframes yellowPulse {
            0%   { box-shadow: 0 0 0 0 rgba(234,179,8,0.7); }
            50%  { box-shadow: 0 0 0 5px rgba(234,179,8,0.15); }
            100% { box-shadow: 0 0 0 0 rgba(234,179,8,0); }
          }
          .hint-pulse { animation: yellowPulse 1s ease-in-out infinite; outline: 2px solid rgba(234,179,8,0.7); outline-offset: -2px; }
          @keyframes hintFadeOut {
            0%, 70% { opacity: 1; }
            100%    { opacity: 0; }
          }
          .hint-tooltip { animation: hintFadeOut 3.5s ease forwards; }
        `}</style>
      )}
      <div ref={tableRef} className="overflow-x-auto rounded-lg border border-white/20">
        <table className="w-full text-sm border-collapse">
          <thead>
            <tr className="border-b border-white/20 bg-gray-800/50">
              <th className="text-left p-3 text-gray-200 font-semibold sticky left-0 bg-gray-800 z-10 w-64 whitespace-nowrap border-r border-white/20 text-base">
                <div className="flex items-center gap-2">
                  <span>{headerTitle}</span>
                  {onAsReportedClick && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onAsReportedClick();
                      }}
                      className="text-blue-400 hover:text-blue-300 text-xs underline ml-2"
                      title="View As Reported"
                    >
                      As Reported
                    </button>
                  )}
                </div>
              </th>
              {columns.map((col, colIdx) => {
                const isAlternate = colIdx % 2 === 1;
                const isFirstCol = colIdx === 0;
                return (
                  <th
                    key={col}
                    className={`text-right p-3 text-gray-300 font-semibold min-w-[120px] ${isAlternate ? 'bg-gray-700/50' : 'bg-gray-800/50'} border-r border-white/20 last:border-r-0 cursor-pointer hover:bg-gray-700/50 hover:text-blue-400 transition-colors relative ${showHint && isFirstCol ? 'hint-pulse' : ''}`}
                    onClick={() => onColumnClick && onColumnClick(col)}
                    title={`View Sankey flow for ${col}`}
                  >
                    {col}
                    {showHint && isFirstCol && (
                      <span className="hint-tooltip pointer-events-none absolute -bottom-6 left-1/2 -translate-x-1/2 bg-yellow-400 text-gray-900 text-[10px] font-semibold px-2 py-0.5 rounded whitespace-nowrap z-50">
                        Click to Visualize
                      </span>
                    )}
                  </th>
                )
              })}
            </tr>
          </thead>
          <tbody>
            {concepts.map((concept, idx) => (
              <tr
                key={concept}
                className={`border-b border-white/20 transition-colors ${idx % 2 === 0 ? 'bg-gray-900/40' : 'bg-gray-900/20'
                  } hover:bg-gray-800/40`}
              >
                <td
                  className={`p-3 pl-6 text-gray-300 font-medium sticky left-0 z-10 w-64 whitespace-nowrap border-r border-white/20 cursor-pointer relative ${idx % 2 === 0 ? 'bg-gray-900' : 'bg-gray-900'
                    } hover:bg-gray-800/50 hover:text-blue-400 transition-colors ${showHint && idx === 0 ? 'hint-pulse' : ''}`}
                  onClick={() => onConceptClick && onConceptClick(concept)}
                  title={concept}
                >
                  {(conceptMeta[concept] && conceptMeta[concept].plabel) || concept}
                  {showHint && idx === 0 && (
                    <span className="hint-tooltip pointer-events-none absolute -bottom-6 left-6 bg-yellow-400 text-gray-900 text-[10px] font-semibold px-2 py-0.5 rounded whitespace-nowrap z-50">
                      Click to Visualize
                    </span>
                  )}
                </td>
                {columns.map((col, colIdx) => {
                  const formatted = formatValue(transformedData[concept][col], concept);
                  const isAlternate = colIdx % 2 === 1;
                  return (
                    <td
                      key={col}
                      className={`text-right p-3 tabular-nums border-r border-white/20 last:border-r-0 ${formatted.isNegative ? 'text-red-400' : 'text-gray-100'
                        } ${isAlternate ? 'bg-gray-700/50' : 'bg-transparent'}`}
                    >
                      {formatted.text}
                    </td>
                  );
                })}
              </tr>
            ))}
            {/* Net Cash Flow row for cashflow statements */}
            {isCashFlow && (
              <tr className="font-bold">
                <td className="p-3 pl-6 text-gray-200 font-bold sticky left-0 z-10 w-64 whitespace-nowrap border-r border-white/20 bg-gray-900">
                  Net Cash Flow
                </td>
                {columns.map((col, colIdx) => {
                  const sum = netCashFlowRow[col] || 0;
                  const conceptLower = 'net cash flow';
                  const isPerShare = conceptLower.includes('per share');
                  const isNegative = sum < 0;
                  const absSum = Math.abs(sum);
                  let formattedSum;
                  const isAlternate = colIdx % 2 === 1;

                  if (sum === null || sum === undefined || isNaN(sum)) {
                    formattedSum = '-';
                  } else if (!isPerShare) {
                    const millionsValue = absSum / 1000000;
                    formattedSum = `$${millionsValue.toLocaleString('en-US', { maximumFractionDigits: 2 })}M`;
                  } else {
                    formattedSum = absSum.toLocaleString('en-US', { maximumFractionDigits: 2 });
                  }

                  // Wrap negative values in brackets
                  if (isNegative) {
                    formattedSum = `(${formattedSum})`;
                  }

                  return (
                    <td
                      key={col}
                      className={`text-right p-3 font-mono tabular-nums font-bold border-r border-white/20 last:border-r-0 ${isNegative ? 'text-red-400' : 'text-gray-100'
                        } ${isAlternate ? 'bg-gray-700/50' : 'bg-transparent'}`}
                    >
                      {formattedSum}
                    </td>
                  );
                })}
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </>
  );
}

// Right Panel with Tabs
// eslint-disable-next-line no-unused-vars
function RightTabPanel() {
  const [activeTab, setActiveTab] = useState("ask");

  const tabs = [
    { id: "ask", label: "Ask Alffy" },
    { id: "summary", label: "AI Summary" },
    { id: "feed", label: "My Feed" },
  ];

  return (
    <div className="flex flex-col h-full bg-gray-900">

      {/* Tab Headers */}
      <div className="flex border-b border-gray-700 flex-shrink-0">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex-1 px-3 py-3 text-xs font-medium transition-colors relative ${activeTab === tab.id
              ? "text-blue-400"
              : "text-gray-500 hover:text-gray-300"
              }`}
          >
            {tab.label}
            {activeTab === tab.id && (
              <div className="absolute bottom-0 left-2 right-2 h-0.5 bg-blue-500 rounded-full" />
            )}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div className="flex-1 overflow-y-auto p-4">
        {activeTab === "ask" && (
          <div className="flex flex-col items-center justify-center h-full text-center">
            <svg className="w-10 h-10 text-gray-600 mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M8.625 12a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0H8.25m4.125 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0H12m4.125 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0h-.375M21 12c0 4.556-4.03 8.25-9 8.25a9.764 9.764 0 01-2.555-.337A5.972 5.972 0 015.41 20.97a5.969 5.969 0 01-.474-.065 4.48 4.48 0 00.978-2.025c.09-.457-.133-.901-.467-1.226C3.93 16.178 3 14.189 3 12c0-4.556 4.03-8.25 9-8.25s9 3.694 9 8.25z" />
            </svg>
            <p className="text-gray-500 text-sm font-medium mb-1">Ask Alffy</p>
            <p className="text-gray-600 text-xs">Ask questions about any stock, ratio, or financial concept</p>
          </div>
        )}

        {activeTab === "summary" && (
          <div className="flex flex-col items-center justify-center h-full text-center">
            <svg className="w-10 h-10 text-gray-600 mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.455 2.456L21.75 6l-1.036.259a3.375 3.375 0 00-2.455 2.456zM16.894 20.567L16.5 21.75l-.394-1.183a2.25 2.25 0 00-1.423-1.423L13.5 18.75l1.183-.394a2.25 2.25 0 001.423-1.423l.394-1.183.394 1.183a2.25 2.25 0 001.423 1.423l1.183.394-1.183.394a2.25 2.25 0 00-1.423 1.423z" />
            </svg>
            <p className="text-gray-500 text-sm font-medium mb-1">AI Summary</p>
            <p className="text-gray-600 text-xs">AI-generated insights and analysis for the selected company</p>
          </div>
        )}

        {activeTab === "feed" && (
          <div className="flex flex-col items-center justify-center h-full text-center">
            <svg className="w-10 h-10 text-gray-600 mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 7.5h1.5m-1.5 3h1.5m-7.5 3h7.5m-7.5 3h7.5m3-9h3.375c.621 0 1.125.504 1.125 1.125V18a2.25 2.25 0 01-2.25 2.25M16.5 7.5V18a2.25 2.25 0 002.25 2.25M16.5 7.5V4.875c0-.621-.504-1.125-1.125-1.125H4.125C3.504 3.75 3 4.254 3 4.875V18a2.25 2.25 0 002.25 2.25h13.5M6 7.5h3v3H6v-3z" />
            </svg>
            <p className="text-gray-500 text-sm font-medium mb-1">My Feed</p>
            <p className="text-gray-600 text-xs">Personalized news and updates for your watchlist stocks</p>
          </div>
        )}
      </div>
    </div>
  );
}


// Watchlist Panel Component
// Watchlist Sign-In CTA Modal (for guest sessions)
function AuthCtaModal({ isOpen, onClose }) {
  if (!isOpen) return null;

  const handleSignIn = () => {
    const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID || "";
    if (!clientId) {
      window.location.href = "/login";
      return;
    }
    const redirectUri = encodeURIComponent(window.location.origin + "/login");
    const nonce = Math.random().toString(36).substring(2, 18);
    window.location.href =
      `https://accounts.google.com/o/oauth2/v2/auth` +
      `?client_id=${clientId}` +
      `&redirect_uri=${redirectUri}` +
      `&response_type=id_token` +
      `&scope=openid%20email%20profile` +
      `&nonce=${nonce}` +
      `&prompt=select_account`;
  };

  return (
    <div className="fixed inset-0 z-[2000] flex items-center justify-center p-4 bg-black/80 backdrop-blur-md animate-in fade-in duration-300">
      <div className="bg-gray-900 border-2 border-blue-500/50 rounded-2xl p-8 max-w-md w-full shadow-2xl relative animate-in zoom-in-95 duration-300">
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-200 text-2xl font-bold transition-colors focus:outline-none"
        >
          &times;
        </button>

        {/* Header */}
        <div className="text-center mb-6">
          <div className="w-16 h-16 bg-blue-500/10 border-2 border-blue-500/30 rounded-2xl flex items-center justify-center mx-auto mb-4 text-blue-400">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-100">Unlock Personalized Experience</h2>
          <p className="text-gray-400 text-sm mt-2 font-medium">
            No credit card required
          </p>
        </div>

        {/* Benefits list */}
        <div className="space-y-3.5 mb-6 text-sm text-gray-300 bg-gray-800/40 p-5 rounded-xl border border-gray-800">
          <div className="flex items-center gap-3">
            <svg className="w-5 h-5 text-green-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M5 13l4 4L19 7" />
            </svg>
            <span>Growth and Valuation Analysis</span>
          </div>
          <div className="flex items-center gap-3">
            <svg className="w-5 h-5 text-green-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M5 13l4 4L19 7" />
            </svg>
            <span>All 10 years data</span>
          </div>
          <div className="flex items-center gap-3">
            <svg className="w-5 h-5 text-green-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M5 13l4 4L19 7" />
            </svg>
            <span>Analyze 4000+ US stocks</span>
          </div>
          <div className="flex items-center gap-3">
            <svg className="w-5 h-5 text-green-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M5 13l4 4L19 7" />
            </svg>
            <span>AI summaries</span>
          </div>
          <div className="flex items-center gap-3">
            <svg className="w-5 h-5 text-green-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M5 13l4 4L19 7" />
            </svg>
            <span>News that matter</span>
          </div>
          <div className="pt-2.5 border-t border-gray-700/50 text-center mt-4">
            <span className="text-base font-bold text-blue-400 tracking-wide">No Card No Strings!</span>
          </div>
        </div>

        {/* Actions */}
        <div className="space-y-3">
          <button
            onClick={handleSignIn}
            className="w-full py-3.5 px-4 bg-gradient-to-r from-yellow-500 to-amber-500 hover:from-yellow-400 hover:to-amber-400 text-gray-900 font-bold rounded-xl shadow-lg shadow-yellow-500/10 hover:shadow-yellow-500/20 active:scale-[0.98] transition-all flex items-center justify-center gap-3"
          >
            {/* Google Logo */}
            <svg className="w-5 h-5 fill-current" viewBox="0 0 24 24">
              <path d="M12.24 10.285V14.4h6.887c-.648 2.41-2.519 4.114-5.136 4.114A5.69 5.69 0 0 1 8.24 12.8a5.69 5.69 0 0 1 5.751-5.714c1.47 0 2.805.518 3.844 1.371l3.02-3.02C18.91 3.518 16.27 2 13.99 2 8.47 2 4 6.47 4 12s4.47 10 9.99 10c5.75 0 9.77-4.04 9.77-9.92 0-.6-.05-1.185-.15-1.795H12.24Z" />
            </svg>
            Sign In with Google
          </button>
          <button
            onClick={onClose}
            className="w-full py-3 px-4 bg-gray-800 hover:bg-gray-700 text-gray-300 font-medium rounded-xl transition-all"
          >
            Continue as Guest
          </button>
        </div>
      </div>
    </div>
  );
}

// Watchlist Panel Component
function WatchlistPanel({ selectedList, onSelectedListChange, refreshKey, onSelectTicker, onAuthCtaOpen, onRefreshed }) {
  const [watchlist, setWatchlist] = useState([]);
  const [availableLists, setAvailableLists] = useState(["Default"]);
  const [localSelectedList, setLocalSelectedList] = useState("Default");
  const activeList = selectedList !== undefined ? selectedList : localSelectedList;
  const setActiveList = onSelectedListChange !== undefined ? onSelectedListChange : setLocalSelectedList;
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isTickerModalOpen, setIsTickerModalOpen] = useState(false);

  const fetchWatchlist = async (listName) => {
    const targetList = listName || activeList;
    try {
      setLoading(true);
      const API_URL = process.env.REACT_APP_API_URL || (process.env.NODE_ENV === "development" ? "http://127.0.0.1:8000" : "");
      let userEmail = localStorage.getItem("userEmail");

      // Fallback: Try to get email from token if missing
      if (!userEmail) {
        const token = localStorage.getItem("token");
        if (token) {
          try {
            const payload = JSON.parse(atob(token.split('.')[1]));
            if (payload.email) {
              userEmail = payload.email;
              localStorage.setItem("userEmail", userEmail);
            }
          } catch (e) {
            console.error("Failed to decode token:", e);
          }
        }
      }

      if (!userEmail) {
        setLoading(false);
        return;
      }

      const response = await fetch(`${API_URL}/user/watchlist?email=${encodeURIComponent(userEmail)}&watchlist_name=${encodeURIComponent(targetList)}`);
      if (!response.ok) {
        throw new Error("Failed to fetch watchlist");
      }
      const data = await response.json();
      setWatchlist(data.watchlist || []);
      if (data.available_lists && data.available_lists.length > 0) {
        setAvailableLists(data.available_lists);
      }
      onRefreshed?.();
    } catch (err) {
      console.error("Error fetching watchlist:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchWatchlist();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeList, refreshKey]);

  // Handle ticker click
  const handleTickerClick = (ticker) => {
    onSelectTicker(ticker);
  };

  // Called when watchlists are created/deleted in the modal
  const handleListsChange = (updatedListNames) => {
    setAvailableLists(updatedListNames);
    // If the currently selected list was deleted, fall back to Default
    if (!updatedListNames.includes(activeList)) {
      setActiveList("Default");
    } else {
      // Re-fetch current list to stay in sync
      fetchWatchlist(activeList);
    }
  };

  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  if (loading && watchlist.length === 0 && !isEditModalOpen && !isTickerModalOpen) return <div className="text-gray-400 p-4 text-xs">Loading...</div>;
  if (error && !isEditModalOpen && !isTickerModalOpen) return <div className="text-red-400 p-4 text-xs">Error: {error}</div>;

  // Split lists: Default always on top, rest in the middle
  const userLists = availableLists.filter((n) => n !== "Default");

  return (
    <div className="bg-gray-900 h-full overflow-y-auto flex flex-col pt-8">
      <div className="px-2">
        {/* Watchlists Label */}
        <div className="flex items-center mb-2 border-b border-transparent">
          <span className="text-sm font-bold text-gray-100">Watchlists</span>
        </div>

        {/* Custom Watchlist Dropdown + Edit Button — blue border wrapper */}
        <div className="flex items-center gap-1.5 border-2 border-blue-500/50 rounded-lg p-1.5">
          <div className="relative flex-1" ref={dropdownRef}>
            <button
              onClick={() => setIsDropdownOpen((prev) => !prev)}
              className="w-full flex items-center justify-between bg-gray-800 text-white text-sm rounded p-2 hover:bg-gray-700 focus:outline-none transition-colors"
            >
              <span className="truncate">{activeList}</span>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className={`h-4 w-4 text-gray-400 transition-transform ${isDropdownOpen ? "rotate-180" : ""}`}
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
              </svg>
            </button>

            {isDropdownOpen && (
              <div className="absolute z-40 mt-1 w-full bg-gray-800 border border-gray-600 rounded-lg shadow-xl overflow-hidden">
                {/* Section 1: Default */}
                <div
                  onClick={() => { setActiveList("Default"); setIsDropdownOpen(false); }}
                  className={`px-3 py-2 text-sm cursor-pointer transition-colors ${activeList === "Default"
                    ? "bg-blue-600/20 text-blue-400"
                    : "text-gray-200 hover:bg-gray-700"
                    }`}
                >
                  Default
                </div>

                {/* Separator */}
                {userLists.length > 0 && (
                  <div className="border-t border-gray-600/50" />
                )}

                {/* Section 2: User watchlists */}
                {userLists.map((name) => (
                  <div
                    key={name}
                    onClick={() => { setActiveList(name); setIsDropdownOpen(false); }}
                    className={`px-3 py-2 text-sm cursor-pointer transition-colors ${activeList === name
                      ? "bg-blue-600/20 text-blue-400"
                      : "text-gray-200 hover:bg-gray-700"
                      }`}
                  >
                    {name}
                  </div>
                ))}

                {/* Separator */}
                <div className="border-t border-gray-600/50" />

                {/* Section 3: Actions */}
                <div
                  onClick={() => {
                    if (localStorage.getItem("isGuest") === "true") {
                      onAuthCtaOpen();
                    } else {
                      setIsEditModalOpen(true);
                    }
                    setIsDropdownOpen(false);
                  }}
                  className="px-3 py-2 text-sm text-gray-400 hover:text-blue-400 hover:bg-gray-700 cursor-pointer transition-colors flex items-center gap-2"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-3.5 w-3.5"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.066 2.573c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.573 1.066c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.066-2.573c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  Manage Watchlists …
                </div>
              </div>
            )}
          </div>
          <button
            onClick={() => {
              if (localStorage.getItem("isGuest") === "true") {
                onAuthCtaOpen();
              } else {
                setIsTickerModalOpen(true);
              }
            }}
            className="p-2 bg-gray-800 rounded hover:bg-gray-700 text-gray-400 hover:text-blue-400 transition-colors flex-shrink-0"
            title="Edit tickers"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-4 w-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
              />
            </svg>
          </button>
        </div>

        {/* Create Watchlist Button (Conversion CTA Trap) */}
        <button
          onClick={() => {
            if (localStorage.getItem("isGuest") === "true") {
              onAuthCtaOpen();
            } else {
              setIsEditModalOpen(true);
            }
          }}
          className="mt-3 w-full py-2.5 px-4 bg-blue-600 hover:bg-blue-500 text-white font-bold text-sm rounded-xl shadow-lg shadow-blue-600/20 hover:shadow-blue-500/30 active:scale-[0.97] transition-all flex items-center justify-center gap-2 focus:outline-none"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
          </svg>
          Create Watchlist
        </button>
      </div>

      {watchlist.length === 0 ? (
        <div className="text-gray-500 p-4 text-xs text-center mt-3">Watchlist is empty</div>
      ) : (
        <div className="mt-3.5 px-2 flex flex-col gap-2 pb-6">
          {watchlist.map((item) => {
            // Calculate position of current price in 52w range
            const low = item.low_52w || 0;
            const high = item.high_52w || 0;
            const price = item.price || 0;
            let percentPosition = 0;

            if (high > low && price >= low) {
              percentPosition = ((price - low) / (high - low)) * 100;
              percentPosition = Math.min(Math.max(percentPosition, 0), 100); // Clamp 0-100
            }

            return (
              <div
                key={item.ticker}
                onClick={() => handleTickerClick(item.ticker)}
                className="p-3.5 bg-gray-800/40 hover:bg-gray-800/80 border border-white/5 hover:border-blue-500/30 rounded-xl cursor-pointer transition-all duration-200 shadow-sm hover:shadow-lg hover:shadow-blue-500/5 active:scale-[0.98] flex flex-col"
              >
                <div className="flex justify-between items-baseline mb-1">
                  <div className="font-bold text-gray-200 text-xs">{item.ticker}</div>
                  <div className="text-right">
                    <div className="text-gray-200 text-xs font-semibold">${item.price.toFixed(2)}</div>
                  </div>
                </div>

                <div className="flex justify-between items-center mb-2">
                  <span className="text-[10px] font-semibold tracking-wider text-gray-500">52W Range</span>
                  <div className={`text-[10px] font-medium ${item.change >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                    {item.change >= 0 ? '+' : ''}{item.change.toFixed(2)} ({item.change_percent.toFixed(2)}%)
                  </div>
                </div>

                {/* 52-Week Range Slider */}
                <div className="relative h-1.5 bg-gray-700 rounded-full mt-2 w-full">
                  {/* Background Bar (Range) */}
                  <div className="absolute top-0 left-0 h-full w-full rounded-full opacity-20"></div>

                  {/* Current Price Marker */}
                  <div
                    className="absolute top-1/2 -translate-y-1/2 h-3.5 w-3.5 bg-blue-500 rounded-full shadow-[0_0_8px_rgba(59,130,246,0.8)] border border-gray-800"
                    style={{ left: `calc(${percentPosition}% - 7px)` }}
                    title={`Price: ${price.toFixed(2)} (Range: ${low.toFixed(2)} - ${high.toFixed(2)})`}
                  ></div>
                </div>
                <div className="flex justify-between text-[10px] text-gray-400 mt-1 font-mono tracking-wide">
                  <span>Lo: ${low.toFixed(0)}</span>
                  <span>Hi: ${high.toFixed(0)}</span>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Edit Modal */}
      <WatchlistEditModal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        onListsChange={handleListsChange}
      />

      {/* Ticker Modal */}
      <WatchlistTickerModal
        isOpen={isTickerModalOpen}
        onClose={() => setIsTickerModalOpen(false)}
        watchlistName={activeList}
        onTickersChange={() => fetchWatchlist(activeList)}
      />
    </div>
  );
}

// Onboarding Video Caption Schedules
const INTRO_OVERLAYS = [
  { start: 0, end: 2, text: "Deep dive with us — no cards, no strings!" },
  { start: 2, end: 5, text: "Unleash your inner Buffett!" },
  { start: 7, end: 12, text: "No noise. Just the fundamentals." },
  { start: 14, end: 21, text: "Visualize everything." },
  { start: 24, end: 31, text: "Build your edge with deep fundamentals." },
  { start: 34, end: 42, text: "Deep dive into valuation." },
  { start: 43, end: 45, text: "Listen to intelligent summaries." },
  { start: 46, end: 52, text: "Deep dive with us — no cards, no strings!" },
];

export default function StockView() {

  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedList, setSelectedList] = useState("Default");
  const [watchlistRefreshKey, setWatchlistRefreshKey] = useState(0);
  const [watchlistTooltip, setWatchlistTooltip] = useState({ show: false, message: "", type: "warning" });
  const [isAddingToWatchlist, setIsAddingToWatchlist] = useState(false);
  const watchlistTooltipTimeoutRef = useRef(null);
  const isWaitingForRefreshRef = useRef(false);

  const getUserEmail = () => {
    let email = localStorage.getItem("userEmail");
    if (!email) {
      const token = localStorage.getItem("token");
      if (token) {
        try {
          const payload = JSON.parse(atob(token.split(".")[1]));
          if (payload.email) {
            email = payload.email;
            localStorage.setItem("userEmail", email);
          }
        } catch (e) {
          /* ignore */
        }
      }
    }
    return email;
  };

  useEffect(() => {
    return () => {
      if (watchlistTooltipTimeoutRef.current) {
        clearTimeout(watchlistTooltipTimeoutRef.current);
      }
    };
  }, []);

  const handleWatchlistRefreshed = () => {
    if (isWaitingForRefreshRef.current) {
      isWaitingForRefreshRef.current = false;
      setIsAddingToWatchlist(false);

      setWatchlistTooltip({
        show: true,
        message: `${selectedCompany?.ticker} added to watchlist`,
        type: "success"
      });
      if (watchlistTooltipTimeoutRef.current) {
        clearTimeout(watchlistTooltipTimeoutRef.current);
      }
      watchlistTooltipTimeoutRef.current = setTimeout(() => {
        setWatchlistTooltip(prev => ({ ...prev, show: false }));
      }, 4000);
    }
  };

  const handleAddWatchlistClick = async () => {
    if (localStorage.getItem("isGuest") === "true") {
      setIsAuthCtaOpen(true);
      return;
    }

    if (!selectedCompany || !selectedCompany.ticker) {
      setWatchlistTooltip({
        show: true,
        message: "No stock selected. Search for a stock to add to watchlist",
        type: "warning"
      });
      if (watchlistTooltipTimeoutRef.current) {
        clearTimeout(watchlistTooltipTimeoutRef.current);
      }
      watchlistTooltipTimeoutRef.current = setTimeout(() => {
        setWatchlistTooltip(prev => ({ ...prev, show: false }));
      }, 4000);
      return;
    }

    const email = getUserEmail();
    if (!email) return;

    setIsAddingToWatchlist(true);
    try {
      const API_URL = process.env.REACT_APP_API_URL || (process.env.NODE_ENV === "development" ? "http://127.0.0.1:8000" : "");
      const res = await fetch(`${API_URL}/watchlists/add-ticker`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, wl_name: selectedList, ticker: selectedCompany.ticker }),
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.detail || "Failed to add stock to watchlist");
      }
      
      // Request active refresh sync from WatchlistPanel
      isWaitingForRefreshRef.current = true;
      setWatchlistRefreshKey(prev => prev + 1);
    } catch (err) {
      console.error("Error adding to watchlist:", err);
      setIsAddingToWatchlist(false);
      isWaitingForRefreshRef.current = false;
      setWatchlistTooltip({
        show: true,
        message: err.message || "Failed to add stock to watchlist",
        type: "warning"
      });
      if (watchlistTooltipTimeoutRef.current) {
        clearTimeout(watchlistTooltipTimeoutRef.current);
      }
      watchlistTooltipTimeoutRef.current = setTimeout(() => {
        setWatchlistTooltip(prev => ({ ...prev, show: false }));
      }, 4000);
    }
  };
  const [isAuthCtaOpen, setIsAuthCtaOpen] = useState(false);
  const [introVideoTime, setIntroVideoTime] = useState(0);
  const currentIntroOverlay = INTRO_OVERLAYS.find(
    o => introVideoTime >= o.start && introVideoTime <= o.end
  );
  const introVideoRef = useRef(null);
  const handleIntroVideoEnded = () => {
    const v = introVideoRef.current;
    if (!v) return;
    v.currentTime = 0;
    v.pause();
    setIntroVideoTime(0);
  };
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [showDropdown, setShowDropdown] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [priceHistory, setPriceHistory] = useState([]);
  const [priceHistoryLoading, setPriceHistoryLoading] = useState(false);
  const [priceHistoryError, setPriceHistoryError] = useState(null);
  const [timeRange, setTimeRange] = useState("6M");
  const [chartType, setChartType] = useState("candle");
  const [isChartMaximized, setIsChartMaximized] = useState(false);
  const [incomeStatementData, setIncomeStatementData] = useState([]);
  const [incomeStatementLoading, setIncomeStatementLoading] = useState(false);
  const [incomeStatementError, setIncomeStatementError] = useState(null);
  const [isIncomeQuarterly, setIsIncomeQuarterly] = useState(false);
  const [isIncomeStatementMaximized, setIsIncomeStatementMaximized] = useState(false);
  const [selectedConcept, setSelectedConcept] = useState(null);
  const [isConceptChartVisible, setIsConceptChartVisible] = useState(false);
  const [conceptChartDataSource, setConceptChartDataSource] = useState(null); // 'income', 'balance', or 'cashflow'
  const [isIncomeStatementCollapsed, setIsIncomeStatementCollapsed] = useState(false);
  const [isAsReportedModalVisible, setIsAsReportedModalVisible] = useState(false);
  const [asReportedData, setAsReportedData] = useState([]);
  const [asReportedLoading, setAsReportedLoading] = useState(false);
  const [asReportedError, setAsReportedError] = useState(null);
  const [balanceSheetData, setBalanceSheetData] = useState([]);
  const [balanceSheetLoading, setBalanceSheetLoading] = useState(false);
  const [balanceSheetError, setBalanceSheetError] = useState(null);
  const [isBalanceSheetQuarterly, setIsBalanceSheetQuarterly] = useState(false);
  const [isBalanceSheetMaximized, setIsBalanceSheetMaximized] = useState(false);
  const [isBalanceSheetCollapsed, setIsBalanceSheetCollapsed] = useState(false);
  const [cashFlowData, setCashFlowData] = useState([]);
  const [cashFlowLoading, setCashFlowLoading] = useState(false);
  const [cashFlowError, setCashFlowError] = useState(null);
  const [isCashFlowQuarterly, setIsCashFlowQuarterly] = useState(false);
  const [isCashFlowMaximized, setIsCashFlowMaximized] = useState(false);
  const [isCashFlowCollapsed, setIsCashFlowCollapsed] = useState(false);
  const [sankeyMapData, setSankeyMapData] = useState([]);
  const [isSankeyVisible, setIsSankeyVisible] = useState(false);
  const [sankeyPeriod, setSankeyPeriod] = useState(null);
  const [companyMetrics, setCompanyMetrics] = useState(null);
  const [companyMetricsLoading, setCompanyMetricsLoading] = useState(false);
  const [companyMetricsError, setCompanyMetricsError] = useState(null);
  const [financialHealth, setFinancialHealth] = useState(null);
  const [financialHealthLoading, setFinancialHealthLoading] = useState(false);
  const [financialHealthError, setFinancialHealthError] = useState(null);
  const [mainTab, setMainTab] = useState("numbers");
  const [newsData, setNewsData] = useState([]);
  const [newsLoading, setNewsLoading] = useState(false);
  const [newsError, setNewsError] = useState(null);
  const [socialData, setSocialData] = useState({ reddit: [], substack: [], x: [] });
  const [socialLoading, setSocialLoading] = useState(false);
  const [mdnaText, setMdnaText] = useState("");
  const [mdnaMarkdown, setMdnaMarkdown] = useState("");
  const [mdnaLoading, setMdnaLoading] = useState(false);
  const [mdnaError, setMdnaError] = useState(null);
  const [growthMetrics, setGrowthMetrics] = useState(null);
  const [growthMetricsLoading, setGrowthMetricsLoading] = useState(false);
  const [growthMetricsError, setGrowthMetricsError] = useState(null);
  const [mdnaFilingInfo, setMdnaFilingInfo] = useState(null);
  const [mdnaSummary, setMdnaSummary] = useState("");
  const [mdnaSummaryLoading, setMdnaSummaryLoading] = useState(false);
  const [mdnaSummaryError, setMdnaSummaryError] = useState(null);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const selectedVoiceRef = useRef(null);
  const [isMdnaFullTextModalOpen, setIsMdnaFullTextModalOpen] = useState(false);
  const [earningsCallData, setEarningsCallData] = useState(null);
  const [earningsCallLoading, setEarningsCallLoading] = useState(false);
  const [earningsCallError, setEarningsCallError] = useState(null);
  const [earningsSummary, setEarningsSummary] = useState("");
  const [earningsSummaryLoading, setEarningsSummaryLoading] = useState(false);
  const [earningsSummaryError, setEarningsSummaryError] = useState(null);
  const [isEarningsSpeaking, setIsEarningsSpeaking] = useState(false);
  const [isEarningsModalOpen, setIsEarningsModalOpen] = useState(false);

  const [peChartRange, setPeChartRange] = useState("10Y");
  const [pbChartRange, setPbChartRange] = useState("5Y");
  const [scatterModal, setScatterModal] = useState(null); // {title, option}
  // In-app news reader: AI summary modal so users stay on alffy instead of
  // bouncing to the publisher's site.
  const [newsModal, setNewsModal] = useState(null); // {url, title, source, snippet, summary, loading, error}
  const searchTimeoutRef = useRef(null);
  const dropdownRef = useRef(null);
  // Preload speech synthesis voices
  useEffect(() => {
    const pickVoice = () => {
      const voices = window.speechSynthesis.getVoices();
      const preferred = ["Alex", "Google US English", "Samantha", "Google UK English Female", "Daniel", "Karen"];
      const best = preferred.map(name => voices.find(v => v.name === name)).find(v => v !== undefined);
      if (best) {
        selectedVoiceRef.current = best;
        console.log("🔊 Selected voice:", best.name);
      }
    };
    pickVoice();
    window.speechSynthesis.onvoiceschanged = pickVoice;
    return () => { window.speechSynthesis.onvoiceschanged = null; };
  }, []);

  const formatCurrency = (value, suffix = "") => {
    if (value === null || value === undefined || Number.isNaN(Number(value))) {
      return "N/A";
    }
    return `$${Number(value).toLocaleString("en-US", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    })}${suffix}`;
  };

  const formatNumber = (value, suffix = "") => {
    if (value === null || value === undefined || Number.isNaN(Number(value))) {
      return "N/A";
    }
    return `${Number(value).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}${suffix}`;
  };

  const formatTitleCase = (value) => {
    if (!value || typeof value !== "string") {
      return "N/A";
    }
    return value
      .toLowerCase()
      .split(/[\s-]+/)
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ")
      .replace(/\bOf\b/g, "of")
      .replace(/\bAnd\b/g, "and");
  };

  const formatCurrencyMillions = (value) => {
    if (value === null || value === undefined || Number.isNaN(Number(value))) {
      return "N/A";
    }
    const millionsValue = Number(value) / 1_000_000;
    return `$${millionsValue.toLocaleString("en-US", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    })}M`;
  };

  const testBigQuery = async () => {
    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const API_URL = process.env.REACT_APP_API_URL || (process.env.NODE_ENV === "development" ? "http://127.0.0.1:8000" : "");
      const response = await fetch(`${API_URL}/test-bigquery`);

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      setResult(data);
    } catch (err) {
      setError(err.message || "Failed to connect to backend");
      console.error("Error testing BigQuery:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Auto-test on component mount
    testBigQuery();
  }, []);

  // Handle click outside dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const searchCompanies = async (query) => {
    if (!query.trim()) {
      setCompanies([]);
      setShowDropdown(false);
      return;
    }

    setSearchLoading(true);
    try {
      const API_URL = process.env.REACT_APP_API_URL || (process.env.NODE_ENV === "development" ? "http://127.0.0.1:8000" : "");
      const response = await fetch(
        `${API_URL}/search-tickers?q=${encodeURIComponent(query)}`
      );
      if (response.ok) {
        const data = await response.json();
        setCompanies(data.companies || []);
        setShowDropdown(true);
      }
    } catch (err) {
      console.error("Error searching companies:", err);
      setCompanies([]);
    } finally {
      setSearchLoading(false);
    }
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchQuery(value);
    setSelectedCompany(null);

    // Debounce search
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(() => {
      searchCompanies(value);
    }, 300);
  };

  const handleSelectCompany = (company) => {
    // Send search event to Google Analytics
    if (typeof window.gtag === 'function') {
      window.gtag('event', 'search', {
        search_term: company.ticker || company.name
      });
    }
    setSelectedCompany(company);
    setSearchQuery(company.name);
    setShowDropdown(false);
    setPriceHistory([]);
    setPriceHistoryError(null);
    setCompanyMetrics(null);
    setCompanyMetricsError(null);
    // Reset MD&A state
    setMdnaText("");
    setMdnaMarkdown("");
    setMdnaError(null);
    setMdnaFilingInfo(null);
    setMdnaSummary("");
    setMdnaSummaryError(null);
    setEarningsCallData(null);
    setEarningsCallError(null);
    setEarningsSummary("");
    setEarningsSummaryError(null);

  };

  useEffect(() => {
    if (selectedCompany && (selectedCompany.ticker || selectedCompany.cik)) {
      const fetchCompanyMetrics = async () => {
        setCompanyMetricsLoading(true);
        setCompanyMetricsError(null);
        try {
          const params = selectedCompany.ticker
            ? `ticker=${encodeURIComponent(selectedCompany.ticker)}`
            : `cik=${encodeURIComponent(selectedCompany.cik)}`;
          const API_URL = process.env.REACT_APP_API_URL || (process.env.NODE_ENV === "development" ? "http://127.0.0.1:8000" : "");
          const response = await fetch(`${API_URL}/company-metrics?${params}`);
          const data = await response.json();

          if (!response.ok) {
            throw new Error(data.error || "Failed to fetch company metrics");
          }

          if (data.error) {
            setCompanyMetricsError(data.error);
            setCompanyMetrics(null);
          } else {
            setCompanyMetrics(data.metrics || null);
          }
        } catch (err) {
          setCompanyMetricsError(err.message || "Failed to fetch company metrics");
          setCompanyMetrics(null);
        } finally {
          setCompanyMetricsLoading(false);
        }
      };

      fetchCompanyMetrics();
    } else {
      setCompanyMetrics(null);
      setCompanyMetricsError(null);
      setCompanyMetricsLoading(false);
    }

  }, [selectedCompany]);

  const fetchedGrowthMetricsRef = useRef(null);

  useEffect(() => {
    if (mainTab === "valuation" && selectedCompany && (selectedCompany.ticker || selectedCompany.cik)) {
      if (fetchedGrowthMetricsRef.current === selectedCompany.cik) return;
      fetchedGrowthMetricsRef.current = selectedCompany.cik;

      const fetchGrowthMetrics = async () => {
        setGrowthMetrics(null); // Clear stale data before fetch
        setGrowthMetricsLoading(true);
        setGrowthMetricsError(null);
        try {
          const params = selectedCompany.ticker
            ? `ticker=${encodeURIComponent(selectedCompany.ticker)}`
            : `cik=${encodeURIComponent(selectedCompany.cik)}`;
          const API_URL = process.env.REACT_APP_API_URL || (process.env.NODE_ENV === "development" ? "http://127.0.0.1:8000" : "");
          const response = await fetch(`${API_URL}/company-growth-metrics?${params}`);
          const data = await response.json();

          if (!response.ok) {
            throw new Error(data.error || "Failed to fetch growth metrics");
          }

          if (data.error) {
            setGrowthMetricsError(data.error);
            setGrowthMetrics(null);
          } else {
            setGrowthMetrics(data.metrics || null);
          }
        } catch (err) {
          setGrowthMetricsError(err.message || "Failed to fetch growth metrics");
          setGrowthMetrics(null);
        } finally {
          setGrowthMetricsLoading(false);
        }
      };

      fetchGrowthMetrics();
    } else if (!selectedCompany) {
      fetchedGrowthMetricsRef.current = null;
    }
  }, [mainTab, selectedCompany]);



  const fetchedFinancialHealthRef = useRef(null);

  // Fetch financial health metrics (TTM calculated values)
  useEffect(() => {
    if (mainTab === "numbers" && selectedCompany && selectedCompany.cik) {
      if (fetchedFinancialHealthRef.current === selectedCompany.cik) return;
      fetchedFinancialHealthRef.current = selectedCompany.cik;

      const fetchFinancialHealth = async () => {
        setFinancialHealthLoading(true);
        setFinancialHealthError(null);
        setFinancialHealth(null);
        try {
          const API_URL = process.env.REACT_APP_API_URL || (process.env.NODE_ENV === "development" ? "http://127.0.0.1:8000" : "");
          const response = await fetch(
            `${API_URL}/ttm-calculated-values?cik=${encodeURIComponent(
              selectedCompany.cik
            )}`
          );
          const data = await response.json();

          if (!response.ok) {
            throw new Error(data.error || "Failed to fetch financial health metrics");
          }

          if (data.error) {
            setFinancialHealthError(data.error);
            setFinancialHealth(null);
          } else {
            setFinancialHealth(data.financial_health || null);
          }
        } catch (err) {
          setFinancialHealthError(err.message || "Failed to fetch financial health metrics");
          setFinancialHealth(null);
        } finally {
          setFinancialHealthLoading(false);
        }
      };

      fetchFinancialHealth();
    } else if (!selectedCompany) {
      setFinancialHealth(null);
      setFinancialHealthError(null);
      setFinancialHealthLoading(false);
      fetchedFinancialHealthRef.current = null;
    }
  }, [mainTab, selectedCompany]);

  // Fetch MD&A when tab switches to "mdna" and a company is selected
  useEffect(() => {
    if (mainTab === "mdna" && selectedCompany && selectedCompany.cik) {
      // Skip if we already have data for this company
      if (mdnaFilingInfo && mdnaMarkdown && !mdnaLoading) {
        return;
      }
      // Set loading states immediately (synchronous)
      setMdnaLoading(true);
      setMdnaError(null);
      setMdnaSummaryLoading(true);
      setMdnaSummaryError(null);

      const fetchAll = async () => {
        const API_URL = process.env.REACT_APP_API_URL || (process.env.NODE_ENV === "development" ? "http://127.0.0.1:8000" : "");

        // Step 1: Fetch full MD&A text first
        let fetchedDataForm = "LATEST";
        try {
          const resp = await fetch(
            `${API_URL}/mdna?cik=${encodeURIComponent(selectedCompany.cik)}&ticker=${encodeURIComponent(selectedCompany.ticker || "")}&form=LATEST`
          );
          const data = await resp.json();
          fetchedDataForm = data.form || "LATEST";
          if (data.error) {
            setMdnaError(data.error);
            setMdnaLoading(false);
            setMdnaSummaryLoading(false);
            return; // Don't try summary if text failed
          }
          setMdnaText(data.mdna_text || "");
          setMdnaMarkdown(data.mdna_markdown || "");
          setMdnaFilingInfo({
            entity_name: data.entity_name,
            form: data.form,
            filing_date: data.filing_date,
            filing_url: data.filing_url,
            mdna_length: data.mdna_length,
          });
          setMdnaLoading(false);
          if (data.summary) {
            setMdnaSummary(data.summary);
            setMdnaSummaryLoading(false);
            return; // Fast path successfully got both!
          }
        } catch (err) {
          setMdnaError(err.message || "Failed to fetch MD&A");
          setMdnaLoading(false);
          setMdnaSummaryLoading(false);
          return;
        }

        // Step 2: Now fetch AI summary (text is available on backend)
        try {
          // Use the precise form returned from Step 1 so the summary cache hits properly
          const resp = await fetch(
            `${API_URL}/mdna-summary?cik=${encodeURIComponent(selectedCompany.cik)}&ticker=${encodeURIComponent(selectedCompany.ticker || "")}&form=${fetchedDataForm}`
          );
          const data = await resp.json();
          if (data.error) {
            setMdnaSummaryError(data.error);
          } else {
            setMdnaSummary(data.summary || "");
          }
        } catch (err) {
          setMdnaSummaryError(err.message || "Failed to generate summary");
        } finally {
          setMdnaSummaryLoading(false);
        }
      };

      fetchAll();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mainTab, selectedCompany]);

  // Fetch Earnings Transcript when tab switches to "mdna"
  useEffect(() => {
    if (mainTab === "mdna" && selectedCompany && selectedCompany.ticker) {
      if (earningsCallData && !earningsCallLoading) return;

      setEarningsCallLoading(true);
      setEarningsCallError(null);
      setEarningsSummaryLoading(true);
      setEarningsSummaryError(null);

      const fetchEarnings = async () => {
        const API_URL = process.env.REACT_APP_API_URL || (process.env.NODE_ENV === "development" ? "http://127.0.0.1:8000" : "");
        try {
          const resp = await fetch(`${API_URL}/earnings-transcript?ticker=${encodeURIComponent(selectedCompany.ticker)}`);
          const data = await resp.json();
          if (data.error) {
            setEarningsCallError(data.error);
            setEarningsSummaryLoading(false);
            return;
          } else {
            setEarningsCallData(data);
            if (data.summary) {
              setEarningsSummary(data.summary);
              setEarningsSummaryLoading(false);
              return; // Fast path successfully got both!
            }
          }
        } catch (err) {
          setEarningsCallError(err.message || "Failed to fetch earnings transcript");
          setEarningsSummaryLoading(false);
          return;
        } finally {
          setEarningsCallLoading(false);
        }

        // Fetch AI Summary
        try {
          const sumResp = await fetch(`${API_URL}/earnings-summary?ticker=${encodeURIComponent(selectedCompany.ticker)}`);
          const sumData = await sumResp.json();
          if (sumData.error) {
            setEarningsSummaryError(sumData.error);
          } else {
            setEarningsSummary(sumData.summary || "");
          }
        } catch (err) {
          setEarningsSummaryError(err.message || "Failed to generate earnings summary");
        } finally {
          setEarningsSummaryLoading(false);
        }
      };

      fetchEarnings();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mainTab, selectedCompany]);

  // Fetch news + social feeds when tab switches to "news"
  useEffect(() => {
    if (mainTab === "news" && selectedCompany && selectedCompany.ticker) {
      const ticker = selectedCompany.ticker;
      const API_URL = process.env.REACT_APP_API_URL || (process.env.NODE_ENV === "development" ? "http://127.0.0.1:8000" : "");
      const sortByDate = (arr) => arr.slice().sort((a, b) => {
        if (!a.date && !b.date) return 0;
        if (!a.date) return 1;
        if (!b.date) return -1;
        return new Date(b.date) - new Date(a.date);
      });

      // Fetch main news
      setNewsLoading(true);
      setNewsError(null);
      setNewsData([]);
      fetch(`${API_URL}/get-news?ticker=${encodeURIComponent(ticker)}`)
        .then(r => r.json())
        .then(data => {
          if (data.error) setNewsError(data.error);
          else setNewsData(sortByDate(data.data || []));
        })
        .catch(err => setNewsError(err.message || "Failed to fetch news"))
        .finally(() => setNewsLoading(false));

      // Fetch social feeds in parallel
      setSocialLoading(true);
      setSocialData({ reddit: [], substack: [], x: [] });
      Promise.allSettled([
        fetch(`${API_URL}/get-feed-reddit?ticker=${encodeURIComponent(ticker)}`).then(r => r.json()),
        fetch(`${API_URL}/get-feed-substack?ticker=${encodeURIComponent(ticker)}`).then(r => r.json()),
        fetch(`${API_URL}/get-feed-x?ticker=${encodeURIComponent(ticker)}`).then(r => r.json()),
      ]).then(([reddit, substack, x]) => {
        setSocialData({
          reddit: sortByDate(reddit.status === 'fulfilled' ? (reddit.value.data || []) : []).slice(0, 4),
          substack: sortByDate(substack.status === 'fulfilled' ? (substack.value.data || []) : []).slice(0, 4),
          x: sortByDate(x.status === 'fulfilled' ? (x.value.data || []) : []).slice(0, 4),
        });
      }).finally(() => setSocialLoading(false));
    }
  }, [mainTab, selectedCompany]);

  // Open in-app AI summary modal for a news/social item. Falls back gracefully
  // if the summary can't be generated — user can still hit "Read original".
  const openNewsSummary = (item) => {
    if (!item || !item.link) return;
    setNewsModal({
      url: item.link,
      title: item.title || "",
      source: item.source || "",
      snippet: item.snippet || "",
      summary: null,
      loading: true,
      error: null,
    });
    const API_URL = process.env.REACT_APP_API_URL || (process.env.NODE_ENV === "development" ? "http://127.0.0.1:8000" : "");
    fetch(`${API_URL}/get-news-summary?url=${encodeURIComponent(item.link)}&title=${encodeURIComponent(item.title || "")}`)
      .then(r => r.json())
      .then(data => {
        setNewsModal(prev => prev && prev.url === item.link ? {
          ...prev,
          loading: false,
          summary: data.summary || null,
          // Backend may surface a cleaner title/source from og: tags.
          title: data.title || prev.title,
          source: data.source || prev.source,
          error: data.summary ? null : (data.message || "Could not summarize this article"),
        } : prev);
      })
      .catch(err => {
        setNewsModal(prev => prev && prev.url === item.link ? {
          ...prev, loading: false, error: err.message || "Failed to fetch summary",
        } : prev);
      });
  };

  useEffect(() => {
    if (selectedCompany && selectedCompany.ticker) {
      const fetchPriceHistory = async () => {
        setPriceHistoryLoading(true);
        setPriceHistoryError(null);
        try {
          const API_URL = process.env.REACT_APP_API_URL || (process.env.NODE_ENV === "development" ? "http://127.0.0.1:8000" : "");
          const response = await fetch(
            `${API_URL}/stock-price-history?ticker=${encodeURIComponent(selectedCompany.ticker)}&time_range=${encodeURIComponent(timeRange)}`
          );

          const data = await response.json();

          if (response.ok) {
            if (data.error) {
              setPriceHistoryError(data.error);
            } else {
              setPriceHistory(data.data || []);
            }
          } else {
            setPriceHistoryError(data.error || "Failed to fetch price history");
          }
        } catch (err) {
          setPriceHistoryError(err.message || "Failed to fetch price history");
        } finally {
          setPriceHistoryLoading(false);
        }
      };
      fetchPriceHistory();
    }
  }, [selectedCompany, timeRange]);

  const fetchedIncomeStatementRef = useRef(null);

  // Fetch income statement when company or quarterly toggle changes
  useEffect(() => {
    if (mainTab === "numbers" && selectedCompany && selectedCompany.cik) {
      const cacheKey = `${selectedCompany.cik}_${isIncomeQuarterly}`;
      if (fetchedIncomeStatementRef.current === cacheKey) return;
      fetchedIncomeStatementRef.current = cacheKey;

      const fetchIncomeStatement = async () => {
        setIncomeStatementLoading(true);
        setIncomeStatementError(null);
        try {
          const API_URL = process.env.REACT_APP_API_URL || (process.env.NODE_ENV === "development" ? "http://127.0.0.1:8000" : "");
          const response = await fetch(
            `${API_URL}/income-statement?cik=${encodeURIComponent(selectedCompany.cik)}&is_quarterly=${isIncomeQuarterly}`
          );

          const data = await response.json();

          if (response.ok) {
            if (data.error) {
              setIncomeStatementError(data.error);
            } else {
              const isData = data.data || [];
              setIncomeStatementData(isData);

              // Detect company type from SIC code and fetch appropriate Sankey map
              const sic = isData.length > 0 && isData[0].sic ? parseInt(isData[0].sic) : null;
              let sankeyType = 'standard';
              if (sic !== null && sic >= 6020 && sic <= 6212) {
                sankeyType = 'bank';
              } else if (sic !== null && sic >= 6311 && sic <= 6411) {
                sankeyType = 'insurance';
              }
              try {
                const sankeyResp = await fetch(`${API_URL}/sankey-map?type=${sankeyType}`);
                const sankeyData = await sankeyResp.json();
                if (sankeyResp.ok && sankeyData.data) {
                  setSankeyMapData(sankeyData.data);
                }
              } catch (sankeyErr) {
                console.error("Error fetching Sankey Map Data:", sankeyErr);
              }
            }
          } else {
            setIncomeStatementError(data.error || "Failed to fetch income statement");
          }
        } catch (err) {
          setIncomeStatementError(err.message || "Failed to fetch income statement");
        } finally {
          setIncomeStatementLoading(false);
        }
      };
      fetchIncomeStatement();
    } else if (!selectedCompany) {
      fetchedIncomeStatementRef.current = null;
    }
  }, [mainTab, selectedCompany, isIncomeQuarterly]);

  // Fetch as-reported income statement when modal opens
  useEffect(() => {
    if (isAsReportedModalVisible && selectedCompany && selectedCompany.cik) {
      const fetchAsReported = async () => {
        setAsReportedLoading(true);
        setAsReportedError(null);
        try {
          const API_URL = process.env.REACT_APP_API_URL || (process.env.NODE_ENV === "development" ? "http://127.0.0.1:8000" : "");
          const response = await fetch(
            `${API_URL}/income-statement-as-reported?cik=${encodeURIComponent(selectedCompany.cik)}&is_quarterly=${isIncomeQuarterly}`
          );

          const data = await response.json();

          if (response.ok) {
            if (data.error) {
              setAsReportedError(data.error);
            } else {
              setAsReportedData(data.data || []);
            }
          } else {
            setAsReportedError(data.error || "Failed to fetch as-reported income statement");
          }
        } catch (err) {
          setAsReportedError(err.message || "Failed to fetch as-reported income statement");
        } finally {
          setAsReportedLoading(false);
        }
      };
      fetchAsReported();
    }
  }, [isAsReportedModalVisible, selectedCompany, isIncomeQuarterly]);

  const fetchedBalanceSheetRef = useRef(null);

  // Fetch balance sheet when company or quarterly toggle changes
  useEffect(() => {
    if (mainTab === "numbers" && selectedCompany && selectedCompany.cik) {
      const cacheKey = `${selectedCompany.cik}_${isBalanceSheetQuarterly}`;
      if (fetchedBalanceSheetRef.current === cacheKey) return;
      fetchedBalanceSheetRef.current = cacheKey;

      const fetchBalanceSheet = async () => {
        setBalanceSheetLoading(true);
        setBalanceSheetError(null);
        try {
          const API_URL = process.env.REACT_APP_API_URL || (process.env.NODE_ENV === "development" ? "http://127.0.0.1:8000" : "");
          const response = await fetch(
            `${API_URL}/balance-sheet?cik=${encodeURIComponent(selectedCompany.cik)}&is_quarterly=${isBalanceSheetQuarterly}`
          );

          const data = await response.json();

          if (response.ok) {
            if (data.error) {
              setBalanceSheetError(data.error);
            } else {
              setBalanceSheetData(data.data || []);
            }
          } else {
            setBalanceSheetError(data.error || "Failed to fetch balance sheet");
          }
        } catch (err) {
          setBalanceSheetError(err.message || "Failed to fetch balance sheet");
        } finally {
          setBalanceSheetLoading(false);
        }
      };
      fetchBalanceSheet();
    } else if (!selectedCompany) {
      fetchedBalanceSheetRef.current = null;
    }
  }, [mainTab, selectedCompany, isBalanceSheetQuarterly]);

  const fetchedCashFlowRef = useRef(null);

  // Fetch cash flow when company or quarterly toggle changes
  useEffect(() => {
    if (mainTab === "numbers" && selectedCompany && selectedCompany.cik) {
      const cacheKey = `${selectedCompany.cik}_${isCashFlowQuarterly}`;
      if (fetchedCashFlowRef.current === cacheKey) return;
      fetchedCashFlowRef.current = cacheKey;

      const fetchCashFlow = async () => {
        setCashFlowLoading(true);
        setCashFlowError(null);
        try {
          const API_URL = process.env.REACT_APP_API_URL || (process.env.NODE_ENV === "development" ? "http://127.0.0.1:8000" : "");
          const response = await fetch(
            `${API_URL}/cash-flow?cik=${encodeURIComponent(selectedCompany.cik)}&is_quarterly=${isCashFlowQuarterly}`
          );

          const data = await response.json();

          if (response.ok) {
            if (data.error) {
              setCashFlowError(data.error);
            } else {
              setCashFlowData(data.data || []);
            }
          } else {
            setCashFlowError(data.error || "Failed to fetch cash flow");
          }
        } catch (err) {
          setCashFlowError(err.message || "Failed to fetch cash flow");
        } finally {
          setCashFlowLoading(false);
        }
      };
      fetchCashFlow();
    } else if (!selectedCompany) {
      fetchedCashFlowRef.current = null;
    }
  }, [mainTab, selectedCompany, isCashFlowQuarterly]);

  const handleWatchlistSelection = async (ticker) => {
    setSearchQuery(ticker);
    setSearchLoading(true);
    try {
      const API_URL = process.env.REACT_APP_API_URL || (process.env.NODE_ENV === "development" ? "http://127.0.0.1:8000" : "");
      const response = await fetch(
        `${API_URL}/search-tickers?q=${encodeURIComponent(ticker)}`
      );
      if (response.ok) {
        const data = await response.json();
        const companies = data.companies || [];
        // Strict: only accept an exact ticker match (case-insensitive)
        const match = companies.find(
          (c) => c.ticker.toUpperCase() === ticker.toUpperCase()
        );
        if (match) {
          handleSelectCompany(match);
        }
      }
    } catch (err) {
      console.error("Error selecting from watchlist:", err);
    } finally {
      setSearchLoading(false);
    }
  };

  // Only show content if there's an error
  const hasError = error || (result && (!result.success || result.error));

  // If successful, show the search interface
  if (!hasError && result && result.success) {
    return (
      <div
        className="flex bg-black text-white select-none" style={{ minHeight: 'calc(100vh - 66px)' }}
        onContextMenu={(e) => e.preventDefault()}
      >
        {/* Watchlist Panel (Left) */}
        <div
          className="w-72 flex-shrink-0 pt-0 px-4 pb-4 border-r border-white/10 hidden md:block"
          style={{ height: 'calc(100vh - 66px)' }}
        >
          <WatchlistPanel
            selectedList={selectedList}
            onSelectedListChange={setSelectedList}
            refreshKey={watchlistRefreshKey}
            onSelectTicker={handleWatchlistSelection}
            onAuthCtaOpen={() => setIsAuthCtaOpen(true)}
            onRefreshed={handleWatchlistRefreshed}
          />
        </div>

        {/* Main Content (Right) */}
        <div className="flex-1 flex flex-col overflow-hidden" style={{ height: 'calc(100vh - 66px)' }}>
          <div className="flex-1 overflow-y-auto bg-gray-900 text-gray-100 p-8">
            <div>
              {/* Top Tabs Bar */}
              <div className="flex gap-6 mb-6 border-b border-gray-700/50" style={{ height: '38px' }}>
                <button
                  className="h-full px-2 text-lg font-bold text-blue-400 relative transition-colors focus:outline-none flex items-center justify-center"
                >
                  Stock View
                  <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-500 rounded-full" />
                </button>
                {/* Additional tabs can be added here in the future */}
              </div>

              {/* Search Box */}
              <div className="relative mb-6 flex gap-4 items-center" ref={dropdownRef}>
                <div className="relative flex-1">
                  <div className="absolute inset-0 bg-gradient-to-r from-blue-600/20 via-purple-600/20 to-blue-600/20 rounded-xl blur-sm"></div>
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={handleSearchChange}
                    placeholder="Search company name (e.g., Apple, Microsoft)..."
                    data-tour="search-box"
                    className="relative w-full p-4 rounded-xl bg-gray-800/90 border-2 border-blue-500/50 text-gray-100 placeholder-gray-400 focus:outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-500/30 shadow-lg backdrop-blur-sm transition-all duration-200"
                  />

                  {/* Loading indicator */}
                  {searchLoading && (
                    <div className="absolute right-4 top-4 text-gray-400 z-10">
                      <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                    </div>
                  )}

                  {/* Dropdown with results */}
                  {showDropdown && companies.length > 0 && (
                    <div className="absolute z-10 w-full mt-2 bg-gray-800 border border-gray-700 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                      {companies.map((company, index) => (
                        <div
                          key={index}
                          onClick={() => handleSelectCompany(company)}
                          className="px-4 py-3 hover:bg-gray-700 cursor-pointer border-b border-gray-700 last:border-b-0"
                        >
                          <div className="flex items-center justify-between">
                            <span className="text-gray-100 font-medium">{company.name}</span>
                            {company.ticker && (
                              <span className="text-blue-400 text-sm ml-2">{company.ticker}</span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {showDropdown && searchQuery && !searchLoading && companies.length === 0 && (
                    <div className="absolute z-10 w-full mt-2 bg-gray-800 border border-gray-700 rounded-lg shadow-lg p-4">
                      <p className="text-gray-400">No companies found</p>
                    </div>
                  )}
                </div>

                {/* Add Watchlist Plus Button (Sign-in CTA Trap) */}
                <div className="relative">
                  {watchlistTooltip.show && (
                    <div className={`absolute bottom-[calc(100%+12px)] right-0 z-50 w-72 p-3.5 bg-gray-800 border rounded-xl shadow-2xl backdrop-blur-md animate-in fade-in slide-in-from-bottom-2 duration-300 ${
                      watchlistTooltip.type === "success" 
                        ? "border-green-500/50 text-green-200" 
                        : "border-amber-500/50 text-amber-200"
                    }`}>
                      <div className={`absolute right-6 -bottom-[6px] w-2.5 h-2.5 bg-gray-800 border-r border-b rotate-45 ${
                        watchlistTooltip.type === "success" ? "border-green-500/50" : "border-amber-500/50"
                      }`}></div>
                      <p className="text-gray-200 font-medium text-xs leading-normal">{watchlistTooltip.message}</p>
                    </div>
                  )}
                  <button
                    onClick={handleAddWatchlistClick}
                    disabled={isAddingToWatchlist}
                    className={`p-4 font-bold rounded-xl shadow-lg transition-all flex items-center justify-center focus:outline-none flex-shrink-0 ${
                      isAddingToWatchlist 
                        ? "bg-gray-700 text-gray-400 cursor-not-allowed border border-gray-600 shadow-none scale-100" 
                        : "bg-blue-600 hover:bg-blue-500 active:scale-[0.97] text-white shadow-blue-600/20 hover:shadow-blue-500/30"
                    }`}
                    title="Add Ticker to Watchlist"
                  >
                    {isAddingToWatchlist ? (
                      <svg className="animate-spin h-6 w-6 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                    ) : (
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
                      </svg>
                    )}
                  </button>
                </div>
              </div>

              {/* Onboarding Video (displayed in Right/Main pane, below the search bar, when no stock is searched) */}
              {!selectedCompany && localStorage.getItem("isGuest") === "true" && (
                <div className="mt-4 flex-1 flex flex-col relative w-full h-[calc(100vh-300px)] min-h-[350px] overflow-hidden rounded-xl bg-black border border-white/5 shadow-2xl">
                  <video
                    ref={introVideoRef}
                    src="/alffy-intro-mobile.mp4"
                    className="w-full h-full object-cover"
                    autoPlay
                    muted
                    playsInline
                    onTimeUpdate={(e) => setIntroVideoTime(e.target.currentTime)}
                    onEnded={handleIntroVideoEnded}
                  />

                  {/* Absolute overlay for caption text at bottom center of the video */}
                  {currentIntroOverlay && (
                    <div
                      key={currentIntroOverlay.start}
                      className="absolute bottom-16 left-1/2 -translate-x-1/2 z-10 px-6 py-3 bg-black/60 backdrop-blur-md border border-white/10 rounded-full text-center max-w-[90%] pointer-events-none animate-in fade-in slide-in-from-bottom-2 duration-300"
                    >
                      <p className="text-white font-medium text-lg leading-tight tracking-wide">
                        {currentIntroOverlay.text}
                      </p>
                    </div>
                  )}
                </div>
              )}

              {/* ── Quick Stats Bar ───────────────────────────── */}
              {selectedCompany && (
                <div className="flex items-center gap-5 px-4 py-2.5 mb-3 bg-gray-800/60 border border-gray-700/60 rounded-xl text-xs">
                  {companyMetricsLoading ? (
                    <span className="text-gray-500 italic">Loading metrics...</span>
                  ) : companyMetrics ? (
                    <>
                      {[
                        { label: "Name", value: selectedCompany.name },
                        { label: "Ticker", value: selectedCompany.ticker },
                        { label: "Price", value: formatCurrency(companyMetrics?.close) },
                        { label: "Market Cap", value: formatCurrency(companyMetrics?.market_cap, "B") },
                        { label: "EPS", value: formatCurrency(companyMetrics?.ttm_eps) },
                        { label: "P/E", value: formatNumber(companyMetrics?.pe) },
                        { label: "P/B", value: formatNumber(companyMetrics?.price_to_book) },
                      ].map(({ label, value }) => (
                        <div key={label} className="flex items-center gap-1.5 whitespace-nowrap">
                          <span className="text-gray-500">{label}:</span>
                          <span className="text-gray-100 font-semibold">{value}</span>
                        </div>
                      ))}
                    </>
                  ) : null}
                </div>
              )}

              {/* ── Main Tab Bar ─────────────────────────────── */}
              {selectedCompany && (
                <div className="flex gap-6 mb-4 border-b border-gray-700/50" style={{ marginTop: '16px' }}>
                  {[
                    { id: "numbers", label: "The Numbers" },
                    { id: "valuation", label: "The Valuation" },
                    { id: "mdna", label: "The Story" },
                    { id: "news", label: "In The News" },
                  ].map((tab) => (
                    <button
                      key={tab.id}
                      onClick={() => setMainTab(tab.id)}
                      className={`pb-2.5 text-base font-medium transition-all duration-200 border-b-2 ${mainTab === tab.id
                        ? "text-blue-400 border-blue-400"
                        : "text-gray-400 border-transparent hover:text-gray-200"
                        }`}
                    >
                      {tab.label}
                    </button>
                  ))}
                </div>
              )}

              {/* ── Numbers Tab Content ──────────────────────── */}
              {mainTab === "numbers" && (
                <>

                  {/* Stock Price History Chart Panel */}
                  {selectedCompany && (
                    <div className="bg-black border border-gray-700 rounded-lg p-4">
                      <div className="flex justify-between items-center mb-3">
                        <h3 className="text-lg font-semibold text-gray-100">Stock Price History</h3>
                        <div className="flex items-center gap-2">
                          {/* Time Range Selection */}
                          <div className="flex items-center gap-1 bg-gray-700 rounded">
                            {["1D", "5D", "1M", "3M", "6M", "YTD", "1Y", "5Y", "All"].map((range) => (
                              <button
                                key={range}
                                onClick={() => setTimeRange(range)}
                                className={`px-3 py-1 text-sm rounded transition-colors ${timeRange === range
                                  ? "bg-blue-600 text-white"
                                  : "text-gray-300 hover:bg-gray-600"
                                  }`}
                              >
                                {range}
                              </button>
                            ))}
                          </div>

                          {/* Chart Type Dropdown */}
                          <select
                            value={chartType}
                            onChange={(e) => setChartType(e.target.value)}
                            className="px-3 py-1 text-sm bg-gray-700 text-gray-100 border border-gray-600 rounded focus:outline-none focus:border-blue-500"
                          >
                            <option value="line">Line</option>
                            <option value="candle">Candle</option>
                          </select>

                          {/* Maximize Button */}
                          <button
                            onClick={() => setIsChartMaximized(true)}
                            className="px-3 py-1 text-sm bg-gray-700 text-gray-100 border border-gray-600 rounded hover:bg-gray-600 transition-colors"
                            title="Maximize chart"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
                            </svg>
                          </button>
                        </div>
                      </div>

                      {priceHistoryLoading && (
                        <div className="flex justify-center items-center py-8">
                          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-400"></div>
                        </div>
                      )}

                      {priceHistoryError && (
                        <div className="bg-red-900 border border-red-700 rounded p-3">
                          <p className="text-red-100">{priceHistoryError}</p>
                        </div>
                      )}

                      {!priceHistoryLoading && !priceHistoryError && priceHistory.length > 0 && (
                        <ChartComponent
                          priceHistory={priceHistory}
                          chartType={chartType}
                          timeRange={timeRange}
                        />
                      )}

                      {!priceHistoryLoading && !priceHistoryError && priceHistory.length === 0 && (
                        <div className="text-center py-8 text-gray-400">
                          <p>No price history data available for the selected time range</p>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Selected Company Display Panel */}
                  {selectedCompany && (
                    <div className="bg-gray-800 border border-gray-700 rounded-lg px-5 py-5 mb-4 mt-4">
                      <h3 className="text-base font-semibold mb-3 text-white">Company Information</h3>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                        <div>
                          <span className="text-gray-500 text-xs block mb-1">Name</span>
                          <p className="text-gray-100 font-medium">{selectedCompany.name || "N/A"}</p>
                        </div>
                        <div>
                          <span className="text-gray-500 text-xs block mb-1">Ticker</span>
                          <p className="text-blue-400 font-semibold">{selectedCompany.ticker || "N/A"}</p>
                        </div>
                        <div>
                          <span className="text-gray-500 text-xs block mb-1">Cik</span>
                          <p className="text-gray-100">{selectedCompany.cik || "N/A"}</p>
                        </div>
                        <div>
                          <span className="text-gray-500 text-xs block mb-1">Industry</span>
                          <p
                            className="text-gray-100 font-medium truncate"
                            title={formatTitleCase(selectedCompany.industry)}
                          >
                            {formatTitleCase(selectedCompany.industry)}
                          </p>
                        </div>
                      </div>

                      <div className="mt-4 border-t border-gray-700 pt-4">
                        <h4 className="text-sm font-semibold text-gray-400 mb-3 uppercase tracking-wide">Latest Market Metrics</h4>

                        {companyMetricsLoading && (
                          <div className="flex justify-center items-center py-4">
                            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-400"></div>
                          </div>
                        )}

                        {companyMetricsError && (
                          <div className="bg-red-900 border border-red-700 rounded p-3 text-sm text-red-100">
                            {companyMetricsError}
                          </div>
                        )}

                        {!companyMetricsLoading && !companyMetricsError && companyMetrics && (
                          <div className="space-y-4 text-sm">
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                              <div>
                                <span className="text-gray-500 text-xs block mb-1">Open</span>
                                <p className="text-gray-100 font-medium">{formatCurrency(companyMetrics?.open)}</p>
                              </div>
                              <div>
                                <span className="text-gray-500 text-xs block mb-1">Close</span>
                                <p className="text-gray-100 font-medium">{formatCurrency(companyMetrics?.close)}</p>
                              </div>
                              <div>
                                <span className="text-gray-500 text-xs block mb-1">High</span>
                                <p className="text-gray-100 font-medium">{formatCurrency(companyMetrics?.high)}</p>
                              </div>
                              <div>
                                <span className="text-gray-500 text-xs block mb-1">Low</span>
                                <p className="text-gray-100 font-medium">{formatCurrency(companyMetrics?.low)}</p>
                              </div>
                            </div>
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                              <div>
                                <span className="text-gray-500 text-xs block mb-1">Market Cap</span>
                                <p className="text-gray-100 font-medium">{formatCurrency(companyMetrics?.market_cap, "B")}</p>
                              </div>
                              <div>
                                <span className="text-gray-500 text-xs block mb-1">TTM EPS</span>
                                <p className="text-gray-100 font-medium">{formatCurrency(companyMetrics?.ttm_eps)}</p>
                              </div>
                              <div>
                                <span className="text-gray-500 text-xs block mb-1">P/E</span>
                                <p className="text-gray-100 font-medium">{formatNumber(companyMetrics?.pe)}</p>
                              </div>
                              <div>
                                <span className="text-gray-500 text-xs block mb-1">Price to Sales</span>
                                <p className="text-gray-100 font-medium">{formatNumber(companyMetrics?.price_to_sales)}</p>
                              </div>
                            </div>
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                              <div>
                                <span className="text-gray-500 text-xs block mb-1">Earnings Before Tax (LTM)</span>
                                <p className="text-gray-100 font-medium">{formatCurrencyMillions(companyMetrics?.latest_ebt)}</p>
                              </div>
                              <div>
                                <span className="text-gray-500 text-xs block mb-1">BVPS</span>
                                <p className="text-gray-100 font-medium">{formatCurrency(companyMetrics?.bvps)}</p>
                              </div>
                              <div>
                                <span className="text-gray-500 text-xs block mb-1">Price to Book</span>
                                <p className="text-gray-100 font-medium">{formatNumber(companyMetrics?.price_to_book)}</p>
                              </div>
                            </div>
                          </div>
                        )}

                        {!companyMetricsLoading && !companyMetricsError && !companyMetrics && (
                          <p className="text-gray-500 text-sm">No metrics available.</p>
                        )}
                      </div>

                      <div className="mt-4 border-t border-gray-700 pt-4">
                        <h4 className="text-sm font-semibold text-gray-400 mb-3 uppercase tracking-wide">Financial Health</h4>

                        {financialHealthLoading && (
                          <div className="flex justify-center items-center py-4">
                            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-400"></div>
                          </div>
                        )}

                        {financialHealthError && (
                          <div className="bg-red-900 border border-red-700 rounded p-3 text-sm text-red-100">
                            {financialHealthError}
                          </div>
                        )}

                        {!financialHealthLoading && !financialHealthError && financialHealth && (
                          <div className="space-y-4 text-sm">
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                              {[
                                {
                                  key: "working_capital",
                                  label: "Working Capital",
                                  value: formatCurrencyMillions(financialHealth?.working_capital),
                                },
                                {
                                  key: "cash_and_equivalents",
                                  label: "Cash & Equivalents",
                                  value: formatCurrencyMillions(financialHealth?.cash_and_equivalents),
                                },
                                {
                                  key: "total_debt",
                                  label: "Total Debt",
                                  value: formatCurrencyMillions(financialHealth?.total_debt),
                                },
                                {
                                  key: "roa",
                                  label: "Return on Assets",
                                  value: financialHealth?.roa ? `${financialHealth.roa.toFixed(2)}%` : 'N/A',
                                },
                              ].map((item) => (
                                <div key={item.key}>
                                  <span className="text-gray-500 text-xs block mb-1">{item.label}</span>
                                  <p className="text-gray-100 text-sm font-medium">{item.value}</p>
                                </div>
                              ))}
                            </div>

                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                              {[
                                {
                                  key: "debt_to_equity",
                                  label: "Debt to Equity",
                                  value: formatNumber(financialHealth?.debt_to_equity),
                                },
                                {
                                  key: "current_ratio",
                                  label: "Current Ratio",
                                  value: formatNumber(financialHealth?.current_ratio),
                                },
                                {
                                  key: "quick_ratio",
                                  label: "Quick Ratio",
                                  value: formatNumber(financialHealth?.quick_ratio),
                                },
                                {
                                  key: "roe",
                                  label: "Return on Equity",
                                  value: financialHealth?.roe ? `${financialHealth.roe.toFixed(2)}%` : 'N/A',
                                },
                              ].map((item) => (
                                <div key={item.key}>
                                  <span className="text-gray-500 text-xs block mb-1">{item.label}</span>
                                  <p className="text-gray-100 text-sm font-medium">{item.value}</p>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {!financialHealthLoading && !financialHealthError && !financialHealth && (
                          <p className="text-gray-500 text-sm">Financial health metrics unavailable.</p>
                        )}
                      </div>
                    </div>
                  )}


                  {/* Income Statement Panel */}
                  {selectedCompany && (
                    <div className="bg-gray-800 border border-gray-700 rounded-lg mt-4 overflow-hidden transition-all duration-300">
                      <div
                        className="flex justify-between items-center p-4 cursor-pointer hover:bg-gray-700/50 transition-colors"
                        onClick={() => setIsIncomeStatementCollapsed(!isIncomeStatementCollapsed)}
                      >
                        <div className="flex items-center gap-3">
                          <svg
                            className={`w-5 h-5 text-gray-400 transition-transform duration-300 ${isIncomeStatementCollapsed ? '' : 'rotate-90'}`}
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                          </svg>
                          <h3 className="text-lg font-semibold text-gray-100" data-tour="financial-table">Income Statement</h3>
                        </div>

                        <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                          {/* Toggle Switch for Quarterly/Annual */}
                          <div className="flex items-center gap-3">
                            <span className={`text-sm ${!isIncomeQuarterly ? 'text-gray-100' : 'text-gray-500'}`}>
                              Annual
                            </span>
                            <label className="relative inline-flex items-center cursor-pointer">
                              <input
                                type="checkbox"
                                checked={isIncomeQuarterly}
                                onChange={(e) => setIsIncomeQuarterly(e.target.checked)}
                                className="sr-only peer"
                              />
                              <div className="w-11 h-6 bg-gray-600 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-800 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                            </label>
                            <span className={`text-sm ${isIncomeQuarterly ? 'text-gray-100' : 'text-gray-500'}`}>
                              Quarterly
                            </span>
                          </div>

                          {/* Maximize Button */}
                          <button
                            onClick={() => setIsIncomeStatementMaximized(true)}
                            className="px-3 py-1 text-sm bg-gray-700 text-gray-100 border border-gray-600 rounded hover:bg-gray-600 transition-colors"
                            title="Maximize income statement"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
                            </svg>
                          </button>
                        </div>
                      </div>

                      <div
                        className={`grid transition-all duration-500 ease-in-out ${isIncomeStatementCollapsed
                          ? 'grid-rows-[0fr] opacity-0'
                          : 'grid-rows-[1fr] opacity-100'
                          }`}
                      >
                        <div className="overflow-hidden">
                          <div className="p-4 pt-0">
                            {incomeStatementLoading && (
                              <div className="flex justify-center items-center py-8">
                                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-400"></div>
                              </div>
                            )}

                            {incomeStatementError && (
                              <div className="bg-red-900 border border-red-700 rounded p-3">
                                <p className="text-red-100">{incomeStatementError}</p>
                              </div>
                            )}

                            {!incomeStatementLoading && !incomeStatementError && incomeStatementData.length > 0 && (
                              <IncomeStatementTable
                                data={incomeStatementData}
                                isQuarterly={isIncomeQuarterly}
                                headerTitle="Normalized"
                                enableHint={true}
                                onAsReportedClick={() => setIsAsReportedModalVisible(true)}
                                onConceptClick={(concept) => {
                                  setSelectedConcept(concept);
                                  setConceptChartDataSource('income');
                                  setIsConceptChartVisible(true);
                                }}
                                onColumnClick={(period) => {
                                  setSankeyPeriod(period);
                                  setIsSankeyVisible(true);
                                }}
                              />
                            )}

                            {!incomeStatementLoading && !incomeStatementError && incomeStatementData.length === 0 && (
                              <div className="text-center py-8 text-gray-400">
                                <p>No income statement data available</p>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Balance Sheet Panel */}
                  {selectedCompany && (
                    <div className="bg-gray-800 border border-gray-700 rounded-lg mt-4 overflow-hidden transition-all duration-300">
                      <div
                        className="flex justify-between items-center p-4 cursor-pointer hover:bg-gray-700/50 transition-colors"
                        onClick={() => setIsBalanceSheetCollapsed(!isBalanceSheetCollapsed)}
                      >
                        <div className="flex items-center gap-3">
                          <svg
                            className={`w-5 h-5 text-gray-400 transition-transform duration-300 ${isBalanceSheetCollapsed ? '' : 'rotate-90'}`}
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                          </svg>
                          <h3 className="text-lg font-semibold text-gray-100">Balance Sheet</h3>
                        </div>

                        <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                          {/* Toggle Switch for Quarterly/Annual */}
                          <div className="flex items-center gap-3">
                            <span className={`text-sm ${!isBalanceSheetQuarterly ? 'text-gray-100' : 'text-gray-500'}`}>
                              Annual
                            </span>
                            <label className="relative inline-flex items-center cursor-pointer">
                              <input
                                type="checkbox"
                                checked={isBalanceSheetQuarterly}
                                onChange={(e) => setIsBalanceSheetQuarterly(e.target.checked)}
                                className="sr-only peer"
                              />
                              <div className="w-11 h-6 bg-gray-600 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-800 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                            </label>
                            <span className={`text-sm ${isBalanceSheetQuarterly ? 'text-gray-100' : 'text-gray-500'}`}>
                              Quarterly
                            </span>
                          </div>

                          {/* Maximize Button */}
                          <button
                            onClick={() => setIsBalanceSheetMaximized(true)}
                            className="px-3 py-1 text-sm bg-gray-700 text-gray-100 border border-gray-600 rounded hover:bg-gray-600 transition-colors"
                            title="Maximize balance sheet"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
                            </svg>
                          </button>
                        </div>
                      </div>

                      <div
                        className={`grid transition-all duration-500 ease-in-out ${isBalanceSheetCollapsed
                          ? 'grid-rows-[0fr] opacity-0'
                          : 'grid-rows-[1fr] opacity-100'
                          }`}
                      >
                        <div className="overflow-hidden">
                          <div className="p-4 pt-0">
                            {balanceSheetLoading && (
                              <div className="flex justify-center items-center py-8">
                                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-400"></div>
                              </div>
                            )}

                            {balanceSheetError && (
                              <div className="bg-red-900 border border-red-700 rounded p-3">
                                <p className="text-red-100">{balanceSheetError}</p>
                              </div>
                            )}

                            {!balanceSheetLoading && !balanceSheetError && balanceSheetData.length > 0 && (
                              <IncomeStatementTable
                                data={balanceSheetData}
                                isQuarterly={isBalanceSheetQuarterly}
                                onConceptClick={(concept) => {
                                  setSelectedConcept(concept);
                                  setConceptChartDataSource('balance');
                                  setIsConceptChartVisible(true);
                                }}
                              />
                            )}

                            {!balanceSheetLoading && !balanceSheetError && balanceSheetData.length === 0 && (
                              <div className="text-center py-8 text-gray-400">
                                <p>No balance sheet data available</p>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Cash Flow Statement Panel */}
                  {selectedCompany && (
                    <div className="bg-gray-800 border border-gray-700 rounded-lg mt-4 overflow-hidden transition-all duration-300">
                      <div
                        className="flex justify-between items-center p-4 cursor-pointer hover:bg-gray-700/50 transition-colors"
                        onClick={() => setIsCashFlowCollapsed(!isCashFlowCollapsed)}
                      >
                        <div className="flex items-center gap-3">
                          <svg
                            className={`w-5 h-5 text-gray-400 transition-transform duration-300 ${isCashFlowCollapsed ? '' : 'rotate-90'}`}
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                          </svg>
                          <h3 className="text-lg font-semibold text-gray-100">Cash Flow Statement</h3>
                        </div>

                        <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                          {/* Toggle Switch for Quarterly/Annual */}
                          <div className="flex items-center gap-3">
                            <span className={`text-sm ${!isCashFlowQuarterly ? 'text-gray-100' : 'text-gray-500'}`}>
                              Annual
                            </span>
                            <label className="relative inline-flex items-center cursor-pointer">
                              <input
                                type="checkbox"
                                checked={isCashFlowQuarterly}
                                onChange={(e) => setIsCashFlowQuarterly(e.target.checked)}
                                className="sr-only peer"
                              />
                              <div className="w-11 h-6 bg-gray-600 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-800 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                            </label>
                            <span className={`text-sm ${isCashFlowQuarterly ? 'text-gray-100' : 'text-gray-500'}`}>
                              Quarterly
                            </span>
                          </div>

                          {/* Maximize Button */}
                          <button
                            onClick={() => setIsCashFlowMaximized(true)}
                            className="px-3 py-1 text-sm bg-gray-700 text-gray-100 border border-gray-600 rounded hover:bg-gray-600 transition-colors"
                            title="Maximize cash flow statement"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
                            </svg>
                          </button>
                        </div>
                      </div>

                      <div
                        className={`grid transition-all duration-500 ease-in-out ${isCashFlowCollapsed
                          ? 'grid-rows-[0fr] opacity-0'
                          : 'grid-rows-[1fr] opacity-100'
                          }`}
                      >
                        <div className="overflow-hidden">
                          <div className="p-4 pt-0">
                            {cashFlowLoading && (
                              <div className="flex justify-center items-center py-8">
                                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-400"></div>
                              </div>
                            )}

                            {cashFlowError && (
                              <div className="bg-red-900 border border-red-700 rounded p-3">
                                <p className="text-red-100">{cashFlowError}</p>
                              </div>
                            )}

                            {!cashFlowLoading && !cashFlowError && cashFlowData.length > 0 && (
                              <IncomeStatementTable
                                data={cashFlowData}
                                isQuarterly={isCashFlowQuarterly}
                                isCashFlow={true}
                                onConceptClick={(concept) => {
                                  setSelectedConcept(concept);
                                  setConceptChartDataSource('cashflow');
                                  setIsConceptChartVisible(true);
                                }}
                              />
                            )}

                            {!cashFlowLoading && !cashFlowError && cashFlowData.length === 0 && (
                              <div className="text-center py-8 text-gray-400">
                                <p>No cash flow statement data available</p>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Maximized Balance Sheet Modal */}
                  {isBalanceSheetMaximized && selectedCompany && balanceSheetData.length > 0 && (
                    <div className="fixed inset-0 z-[1100] bg-gray-900 bg-opacity-95 flex flex-col">
                      <div className="flex justify-between items-center p-4 border-b border-gray-700">
                        <h2 className="text-xl font-semibold text-gray-100">
                          {selectedCompany.name} ({selectedCompany.ticker}) - Balance Sheet
                        </h2>
                        <div className="flex items-center gap-4">
                          {/* Toggle Switch for Quarterly/Annual */}
                          <div className="flex items-center gap-3">
                            <span className={`text-sm ${!isBalanceSheetQuarterly ? 'text-gray-100' : 'text-gray-500'}`}>
                              Annual
                            </span>
                            <label className="relative inline-flex items-center cursor-pointer">
                              <input
                                type="checkbox"
                                checked={isBalanceSheetQuarterly}
                                onChange={(e) => setIsBalanceSheetQuarterly(e.target.checked)}
                                className="sr-only peer"
                              />
                              <div className="w-11 h-6 bg-gray-600 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-800 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                            </label>
                            <span className={`text-sm ${isBalanceSheetQuarterly ? 'text-gray-100' : 'text-gray-500'}`}>
                              Quarterly
                            </span>
                          </div>

                          {/* Collapse Button */}
                          <button
                            onClick={() => setIsBalanceSheetMaximized(false)}
                            className="px-4 py-2 bg-gray-700 text-gray-100 border border-gray-600 rounded hover:bg-gray-600 transition-colors"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        </div>
                      </div>
                      <div className="flex-1 p-4 overflow-auto">
                        <IncomeStatementTable
                          data={balanceSheetData}
                          isQuarterly={isBalanceSheetQuarterly}
                          isMaximized={true}
                          onConceptClick={(concept) => {
                            setSelectedConcept(concept);
                            setConceptChartDataSource('balance');
                            setIsConceptChartVisible(true);
                          }}
                        />
                      </div>
                    </div>
                  )}

                  {/* Maximized Cash Flow Statement Modal */}
                  {isCashFlowMaximized && selectedCompany && cashFlowData.length > 0 && (
                    <div className="fixed inset-0 z-[1100] bg-gray-900 bg-opacity-95 flex flex-col">
                      <div className="flex justify-between items-center p-4 border-b border-gray-700">
                        <h2 className="text-xl font-semibold text-gray-100">
                          {selectedCompany.name} ({selectedCompany.ticker}) - Cash Flow Statement
                        </h2>
                        <div className="flex items-center gap-4">
                          {/* Toggle Switch for Quarterly/Annual */}
                          <div className="flex items-center gap-3">
                            <span className={`text-sm ${!isCashFlowQuarterly ? 'text-gray-100' : 'text-gray-500'}`}>
                              Annual
                            </span>
                            <label className="relative inline-flex items-center cursor-pointer">
                              <input
                                type="checkbox"
                                checked={isCashFlowQuarterly}
                                onChange={(e) => setIsCashFlowQuarterly(e.target.checked)}
                                className="sr-only peer"
                              />
                              <div className="w-11 h-6 bg-gray-600 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-800 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                            </label>
                            <span className={`text-sm ${isCashFlowQuarterly ? 'text-gray-100' : 'text-gray-500'}`}>
                              Quarterly
                            </span>
                          </div>

                          {/* Collapse Button */}
                          <button
                            onClick={() => setIsCashFlowMaximized(false)}
                            className="px-4 py-2 bg-gray-700 text-gray-100 border border-gray-600 rounded hover:bg-gray-600 transition-colors"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        </div>
                      </div>
                      <div className="flex-1 p-4 overflow-auto">
                        <IncomeStatementTable
                          data={cashFlowData}
                          isQuarterly={isCashFlowQuarterly}
                          isMaximized={true}
                          isCashFlow={true}
                          onConceptClick={(concept) => {
                            setSelectedConcept(concept);
                            setConceptChartDataSource('cashflow');
                            setIsConceptChartVisible(true);
                          }}
                        />
                      </div>
                    </div>
                  )}

                  {/* Maximized Income Statement Modal */}
                  {isIncomeStatementMaximized && selectedCompany && incomeStatementData.length > 0 && (
                    <div className="fixed inset-0 z-[1100] bg-gray-900 bg-opacity-95 flex flex-col">
                      <div className="flex justify-between items-center p-4 border-b border-gray-700">
                        <h2 className="text-xl font-semibold text-gray-100">
                          {selectedCompany.name} ({selectedCompany.ticker}) - Income Statement
                        </h2>
                        <div className="flex items-center gap-4">
                          {/* Toggle Switch for Quarterly/Annual */}
                          <div className="flex items-center gap-3">
                            <span className={`text-sm ${!isIncomeQuarterly ? 'text-gray-100' : 'text-gray-500'}`}>
                              Annual
                            </span>
                            <label className="relative inline-flex items-center cursor-pointer">
                              <input
                                type="checkbox"
                                checked={isIncomeQuarterly}
                                onChange={(e) => setIsIncomeQuarterly(e.target.checked)}
                                className="sr-only peer"
                              />
                              <div className="w-11 h-6 bg-gray-600 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-800 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                            </label>
                            <span className={`text-sm ${isIncomeQuarterly ? 'text-gray-100' : 'text-gray-500'}`}>
                              Quarterly
                            </span>
                          </div>

                          {/* Collapse Button */}
                          <button
                            onClick={() => setIsIncomeStatementMaximized(false)}
                            className="px-4 py-2 bg-gray-700 text-gray-100 border border-gray-600 rounded hover:bg-gray-600 transition-colors"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        </div>
                      </div>
                      <div className="flex-1 p-4 overflow-auto">
                        <IncomeStatementTable
                          data={incomeStatementData}
                          isQuarterly={isIncomeQuarterly}
                          isMaximized={true}
                          onConceptClick={(concept) => {
                            setSelectedConcept(concept);
                            setConceptChartDataSource('income');
                            setIsConceptChartVisible(true);
                          }}
                          onColumnClick={(period) => {
                            setSankeyPeriod(period);
                            setIsSankeyVisible(true);
                          }}
                        />
                      </div>
                    </div>
                  )}

                  {/* As Reported Income Statement Modal */}
                  {isAsReportedModalVisible && selectedCompany && (
                    <div className="fixed inset-0 z-[1100] bg-gray-900 bg-opacity-95 flex flex-col">
                      <div className="flex justify-between items-center p-4 border-b border-gray-700">
                        <h2 className="text-xl font-semibold text-gray-100">
                          {selectedCompany.name} ({selectedCompany.ticker}) - Income Statement (As Reported)
                        </h2>
                        <div className="flex items-center gap-4">
                          {/* Toggle Switch for Quarterly/Annual */}
                          <div className="flex items-center gap-3">
                            <span className={`text-sm ${!isIncomeQuarterly ? 'text-gray-100' : 'text-gray-500'}`}>
                              Annual
                            </span>
                            <label className="relative inline-flex items-center cursor-pointer">
                              <input
                                type="checkbox"
                                checked={isIncomeQuarterly}
                                onChange={(e) => setIsIncomeQuarterly(e.target.checked)}
                                className="sr-only peer"
                              />
                              <div className="w-11 h-6 bg-gray-600 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-800 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                            </label>
                            <span className={`text-sm ${isIncomeQuarterly ? 'text-gray-100' : 'text-gray-500'}`}>
                              Quarterly
                            </span>
                          </div>
                          <button
                            onClick={() => setIsAsReportedModalVisible(false)}
                            className="text-gray-400 hover:text-gray-200 text-2xl font-bold"
                          >
                            ×
                          </button>
                        </div>
                      </div>
                      <div className="flex-1 overflow-auto p-4">
                        {asReportedLoading && (
                          <div className="flex justify-center items-center py-8">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-400"></div>
                          </div>
                        )}

                        {asReportedError && (
                          <div className="bg-red-900 border border-red-700 rounded p-3">
                            <p className="text-red-100">{asReportedError}</p>
                          </div>
                        )}

                        {!asReportedLoading && !asReportedError && asReportedData.length > 0 && (
                          <IncomeStatementTable
                            data={asReportedData}
                            isQuarterly={isIncomeQuarterly}
                            isMaximized={true}
                            headerTitle="As Reported"
                          />
                        )}

                        {!asReportedLoading && !asReportedError && asReportedData.length === 0 && (
                          <div className="text-center py-8 text-gray-400">
                            <p>No as-reported income statement data available</p>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Concept Visualization Modal */}
                  {isConceptChartVisible && selectedConcept && (
                    <div className="fixed inset-0 z-[1100] bg-gray-900 bg-opacity-95 flex items-center justify-center">
                      <div className="bg-gray-800 border border-gray-700 rounded-lg p-6 w-11/12 max-w-4xl max-h-[90vh] overflow-auto">
                        <div className="flex justify-between items-center mb-4">
                          <h2 className="text-xl font-semibold text-gray-100">
                            {selectedConcept}
                          </h2>
                          <button
                            onClick={() => {
                              setIsConceptChartVisible(false);
                              setSelectedConcept(null);
                              setConceptChartDataSource(null);
                            }}
                            className="px-4 py-2 bg-gray-700 text-gray-100 border border-gray-600 rounded hover:bg-gray-600 transition-colors"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        </div>
                        <ConceptChart
                          data={
                            conceptChartDataSource === 'income'
                              ? incomeStatementData
                              : conceptChartDataSource === 'balance'
                                ? balanceSheetData
                                : cashFlowData
                          }
                          concept={selectedConcept}
                          isQuarterly={
                            conceptChartDataSource === 'income'
                              ? isIncomeQuarterly
                              : conceptChartDataSource === 'balance'
                                ? isBalanceSheetQuarterly
                                : isCashFlowQuarterly
                          }
                        />
                      </div>
                    </div>
                  )}

                  {/* Sankey Chart Modal */}
                  {isSankeyVisible && sankeyPeriod && (
                    <SankeyChart
                      data={incomeStatementData}
                      yearOrPeriod={sankeyPeriod}
                      isQuarterly={isIncomeQuarterly}
                      sankeyMapData={sankeyMapData}
                      onClose={() => {
                        setIsSankeyVisible(false);
                        setSankeyPeriod(null);
                      }}
                    />
                  )}

                  {/* Maximized Chart Modal */}
                  {isChartMaximized && selectedCompany && priceHistory.length > 0 && (
                    <div className="fixed inset-0 z-[1100] bg-black flex flex-col">
                      <div className="flex justify-between items-center p-4 border-b border-gray-700">
                        <h2 className="text-xl font-semibold text-gray-100">
                          {selectedCompany.name} ({selectedCompany.ticker}) - Stock Price History
                        </h2>
                        <div className="flex items-center gap-4">
                          {/* Time Range Selection */}
                          <div className="flex items-center gap-1 bg-gray-700 rounded">
                            {["1D", "5D", "1M", "3M", "6M", "YTD", "1Y", "5Y", "All"].map((range) => (
                              <button
                                key={range}
                                onClick={() => setTimeRange(range)}
                                className={`px-3 py-1 text-sm rounded transition-colors ${timeRange === range
                                  ? "bg-blue-600 text-white"
                                  : "text-gray-300 hover:bg-gray-600"
                                  }`}
                              >
                                {range}
                              </button>
                            ))}
                          </div>

                          {/* Chart Type Dropdown */}
                          <select
                            value={chartType}
                            onChange={(e) => setChartType(e.target.value)}
                            className="px-3 py-1 text-sm bg-gray-700 text-gray-100 border border-gray-600 rounded focus:outline-none focus:border-blue-500"
                          >
                            <option value="line">Line</option>
                            <option value="candle">Candle</option>
                          </select>

                          {/* Collapse Button */}
                          <button
                            onClick={() => setIsChartMaximized(false)}
                            className="px-4 py-2 bg-gray-700 text-gray-100 border border-gray-600 rounded hover:bg-gray-600 transition-colors"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        </div>
                      </div>
                      <div className="flex-1 p-4">
                        <ChartComponent
                          priceHistory={priceHistory}
                          chartType={chartType}
                          timeRange={timeRange}
                          isMaximized={true}
                        />
                      </div>
                    </div>
                  )}

                  {/* ── End Numbers Tab ──────────────────────────── */}
                </>
              )}

              {/* ── Valuation Tab Content ─────────────────────── */}
              {mainTab === "valuation" && (
                <>
                  {selectedCompany && (
                    <div className="space-y-4">

                      {growthMetricsLoading && (
                        <div className="flex justify-center items-center py-4">
                          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-400"></div>
                        </div>
                      )}

                      {growthMetricsError && (
                        <div className="bg-red-900 border border-red-700 rounded p-3 text-sm text-red-100">
                          {growthMetricsError}
                        </div>
                      )}

                      {!growthMetricsLoading && !growthMetricsError && growthMetrics && (
                        <>
                          {/* Growth Panel */}
                          <div className="bg-gray-800 border border-gray-700 rounded-lg px-5 py-5">
                            <h3 className="text-base font-semibold mb-3 text-white">Growth</h3>
                            <div className="flex gap-4">
                              {/* 60% — 3 vertical CAGR lollipop charts */}
                              <div className="w-[60%] grid grid-cols-3 gap-3">
                                {/* ── Lollipop chart helper ───────────────────────────────────── */}
                                {(() => {
                                  // Builds an ECharts lollipop (dumbbell) option for vertical CAGR display
                                  const lollipop = (rows) => {
                                    // rows: [{label, val, color}]
                                    const valid = rows.filter(r => r.val != null);
                                    if (valid.length === 0) return null;

                                    const minV = Math.min(0, ...valid.map(r => r.val));
                                    const maxV = Math.max(0, ...valid.map(r => r.val));
                                    const pad = Math.max(Math.abs(maxV) * 0.25, 3);

                                    return {
                                      backgroundColor: 'transparent',
                                      animation: true,
                                      grid: { top: 22, right: 8, bottom: 20, left: 28, containLabel: false },
                                      xAxis: {
                                        type: 'category',
                                        data: rows.map(r => r.label),
                                        axisLabel: { color: '#9ca3af', fontSize: 10, fontWeight: '600' },
                                        axisLine: { lineStyle: { color: '#374151' } },
                                        axisTick: { show: false },
                                      },
                                      yAxis: {
                                        type: 'value',
                                        min: minV < 0 ? minV - pad : 0,
                                        max: maxV + pad,
                                        axisLabel: {
                                          color: '#9ca3af', fontSize: 10,
                                          formatter: v => `${Math.round(v)}%`,
                                        },
                                        splitLine: { lineStyle: { color: '#1f2937', type: 'dashed' } },
                                        axisLine: { show: false },
                                      },
                                      series: [
                                        // Stem — thin coloured bar
                                        {
                                          type: 'bar',
                                          barWidth: 2,
                                          data: rows.map(r => ({
                                            value: r.val ?? 0,
                                            itemStyle: {
                                              color: r.val == null ? '#374151'
                                                : r.val >= 0 ? r.color : '#ef4444',
                                              borderRadius: 1,
                                            },
                                          })),
                                          z: 1,
                                          silent: true,
                                        },
                                        // Dot — scatter overlay at bar tip
                                        {
                                          type: 'scatter',
                                          data: rows.map(r => ({
                                            value: [r.label, r.val ?? 0],
                                            itemStyle: {
                                              color: r.val == null ? '#374151'
                                                : r.val >= 0 ? r.color : '#ef4444',
                                            },
                                          })),
                                          symbolSize: 13,
                                          label: {
                                            show: true,
                                            formatter: p => {
                                              const v = p.data.value[1];
                                              return v == null || v === 0 ? 'N/A' : `${v.toFixed(1)}%`;
                                            },
                                            position: 'top',
                                            color: 'inherit',
                                            fontSize: 9,
                                            fontWeight: 'bold',
                                            distance: 4,
                                          },
                                          z: 2,
                                        },
                                      ],
                                      tooltip: {
                                        trigger: 'item', backgroundColor: '#111827',
                                        borderColor: '#374151',
                                        textStyle: { color: '#e5e7eb', fontSize: 11 },
                                        formatter: p => {
                                          const v = Array.isArray(p.data.value) ? p.data.value[1] : p.data.value;
                                          return `${p.name || p.data.value?.[1]}: <b>${v != null ? v.toFixed(2) + '%' : 'N/A'}</b>`;
                                        },
                                      },
                                    };
                                  };

                                  // Chart-level colours: one colour per metric (all lollipops same within a chart)
                                  const REV_COLOR = '#22c55e';  // green  — Revenue
                                  const NET_COLOR = '#3b82f6';  // blue   — Net Profit
                                  const PRICE_COLOR = '#f59e0b';  // orange — Stock Price

                                  const revRows = [
                                    { label: '10Y', val: growthMetrics.revenue_cagr?.['10y'], color: REV_COLOR },
                                    { label: '5Y', val: growthMetrics.revenue_cagr?.['5y'], color: REV_COLOR },
                                    { label: '3Y', val: growthMetrics.revenue_cagr?.['3y'], color: REV_COLOR },
                                    { label: 'TTM', val: growthMetrics.revenue_cagr?.ttm, color: REV_COLOR },
                                  ];
                                  const netRows = [
                                    { label: '10Y', val: growthMetrics.net_profit_cagr?.['10y'], color: NET_COLOR },
                                    { label: '5Y', val: growthMetrics.net_profit_cagr?.['5y'], color: NET_COLOR },
                                    { label: '3Y', val: growthMetrics.net_profit_cagr?.['3y'], color: NET_COLOR },
                                    { label: 'TTM', val: growthMetrics.net_profit_cagr?.ttm, color: NET_COLOR },
                                  ];
                                  const priceRows = [
                                    { label: '10Y', val: growthMetrics.price_cagr?.['10y'], color: PRICE_COLOR },
                                    { label: '5Y', val: growthMetrics.price_cagr?.['5y'], color: PRICE_COLOR },
                                    { label: '3Y', val: growthMetrics.price_cagr?.['3y'], color: PRICE_COLOR },
                                    { label: '1Y', val: growthMetrics.price_cagr?.['1y'], color: PRICE_COLOR },
                                  ];

                                  const Card = ({ title, rows }) => {
                                    const opt = lollipop(rows);
                                    return (
                                      <div className="bg-gray-900/60 border border-gray-700/50 rounded-lg p-4">
                                        <span className="text-gray-100 text-xs block mb-1 font-semibold tracking-wider">{title}</span>
                                        {opt
                                          ? <ReactECharts style={{ height: '210px', width: '100%' }} option={opt} />
                                          : <p className="text-xs text-gray-500 mt-4">No data available</p>
                                        }
                                      </div>
                                    );
                                  };

                                  return (
                                    <>
                                      <Card title="Revenue CAGR" rows={revRows} />
                                      <Card title="Net Profit CAGR" rows={netRows} />
                                      <Card title="Stock Price CAGR" rows={priceRows} />
                                    </>
                                  );
                                })()}
                              </div>{/* /60% CAGR charts */}

                              {/* 40% — Slope / comparison line chart */}
                              {(() => {
                                // periods: 10Y, 5Y, 3Y, TTM (Stock Price uses 1Y at TTM slot)
                                const periods = ['10Y', '5Y', '3Y', 'TTM'];

                                const revVals = [
                                  growthMetrics.revenue_cagr?.['10y'],
                                  growthMetrics.revenue_cagr?.['5y'],
                                  growthMetrics.revenue_cagr?.['3y'],
                                  growthMetrics.revenue_cagr?.ttm,
                                ];
                                const netVals = [
                                  growthMetrics.net_profit_cagr?.['10y'],
                                  growthMetrics.net_profit_cagr?.['5y'],
                                  growthMetrics.net_profit_cagr?.['3y'],
                                  growthMetrics.net_profit_cagr?.ttm,
                                ];
                                const priceVals = [
                                  growthMetrics.price_cagr?.['10y'],
                                  growthMetrics.price_cagr?.['5y'],
                                  growthMetrics.price_cagr?.['3y'],
                                  growthMetrics.price_cagr?.['1y'],  // 1Y maps to TTM slot
                                ];

                                const allVals = [...revVals, ...netVals, ...priceVals].filter(v => v != null);
                                if (allVals.length === 0) return <div className="w-[40%]" />;

                                const minV = Math.min(...allVals);
                                const maxV = Math.max(...allVals);
                                const pad = Math.max((maxV - minV) * 0.35, 4);

                                const makeSeries = (name, vals, color) => ({
                                  name,
                                  type: 'line',
                                  smooth: true,
                                  data: vals.map(v => v ?? null),
                                  connectNulls: false,
                                  lineStyle: { color, width: 2.5 },
                                  itemStyle: { color, borderColor: color, borderWidth: 2 },
                                  symbol: 'circle',
                                  symbolSize: 10,
                                  label: { show: false },
                                  emphasis: { focus: 'series' },
                                });

                                const slopeOption = {
                                  backgroundColor: 'transparent',
                                  animation: true,
                                  grid: { top: 28, right: 16, bottom: 20, left: 32, containLabel: false },
                                  xAxis: {
                                    type: 'category',
                                    data: periods,
                                    boundaryGap: true,
                                    axisLabel: { color: '#9ca3af', fontSize: 9, fontWeight: '600' },
                                    axisLine: { lineStyle: { color: '#374151' } },
                                    axisTick: { show: false },
                                    splitLine: {
                                      show: true,
                                      lineStyle: { color: '#374151', type: 'solid' },
                                    },
                                  },
                                  yAxis: {
                                    type: 'value',
                                    min: minV - pad,
                                    max: maxV + pad,
                                    axisLabel: {
                                      color: '#9ca3af', fontSize: 10,
                                      formatter: v => `${Math.round(v)}%`,
                                    },
                                    axisLine: { show: false },
                                    axisTick: { show: false },
                                    splitLine: {
                                      show: true,
                                      lineStyle: { color: '#1f2937', type: 'dashed' },
                                    },
                                  },
                                  legend: {
                                    top: 0,
                                    right: 0,
                                    textStyle: { color: '#d1d5db', fontSize: 9 },
                                    itemWidth: 12, itemHeight: 3,
                                    icon: 'rect',
                                  },
                                  tooltip: {
                                    trigger: 'axis',
                                    backgroundColor: '#111827',
                                    borderColor: '#374151',
                                    textStyle: { color: '#e5e7eb', fontSize: 11 },
                                    formatter: params => {
                                      const period = periods[params[0]?.dataIndex] || '';
                                      return `<b>${period}</b><br/>` + params
                                        .filter(p => p.value != null)
                                        .map(p => `<span style="color:${p.color}">●</span> ${p.seriesName}: <b>${Number(p.value).toFixed(2)}%</b>`)
                                        .join('<br/>');
                                    },
                                  },
                                  series: [
                                    makeSeries('Revenue', revVals, '#22c55e'),
                                    makeSeries('Net Profit', netVals, '#3b82f6'),
                                    makeSeries('Stock Price', priceVals, '#f59e0b'),
                                  ],
                                };

                                return (
                                  <div className="w-[40%] bg-gray-900/60 border border-gray-700/50 rounded-lg p-4">
                                    <span className="text-gray-100 text-xs block mb-1 font-semibold tracking-wider">CAGR Comparison</span>
                                    <ReactECharts style={{ height: '210px', width: '100%' }} option={slopeOption} />
                                  </div>
                                );
                              })()}

                            </div>{/* /flex 60-40 */}

                          </div>

                          {/* Price to Earnings (P/E) Panel */}
                          <div className="bg-gray-800 border border-gray-700 rounded-lg px-5 py-5">
                            <h3 className="text-base font-semibold mb-6 text-white">Price to Earnings (P/E)</h3>
                            <div className="flex gap-4">
                              {/* Column 1: Historical P/E line chart */}
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 mb-2">
                                  <span className="text-gray-100 text-xs font-semibold tracking-wider">Historical P/E</span>
                                  {growthMetrics.valuation_history?.pe?.timeseries?.length > 0 && (
                                    <div className="flex items-center gap-0.5 bg-gray-900/60 rounded p-0.5">
                                      {["3Y", "5Y", "10Y"].map(r => (
                                        <button
                                          key={r}
                                          onClick={() => setPeChartRange(r)}
                                          className={`px-1.5 py-0.5 text-[9px] font-semibold rounded transition-colors ${peChartRange === r ? 'bg-blue-600 text-white' : 'text-gray-400 hover:text-gray-200'
                                            }`}
                                        >
                                          {r}
                                        </button>
                                      ))}
                                    </div>
                                  )}
                                </div>
                                {(() => {
                                  const allTs = growthMetrics.valuation_history?.pe?.timeseries || [];
                                  if (allTs.length === 0) return <p className="text-sm font-bold text-gray-400">N/A</p>;
                                  const yearsBack = peChartRange === "3Y" ? 3 : peChartRange === "5Y" ? 5 : 10;
                                  const cutoff = new Date();
                                  cutoff.setFullYear(cutoff.getFullYear() - yearsBack);
                                  const cutoffStr = `${cutoff.getFullYear()}-${String(cutoff.getMonth() + 1).padStart(2, '0')}`;
                                  const filtered = allTs.filter(d => d.date >= cutoffStr);
                                  const displayTs = filtered.length > 0 ? filtered : allTs;
                                  const validPe = displayTs.filter(d => d.pe != null).map(d => d.pe);
                                  const periodAvg = validPe.length > 0 ? Math.round((validPe.reduce((a, b) => a + b, 0) / validPe.length) * 10) / 10 : null;
                                  // Show only year on x-axis — deduplicate so each year label shows once
                                  const xLabels = displayTs.map(d => d.date.substring(0, 4));
                                  const labelInterval = Math.max(0, Math.floor(displayTs.length / 5) - 1);
                                  return (
                                    <>
                                      <ReactECharts
                                        style={{ height: '220px', width: '100%' }}
                                        option={{
                                          backgroundColor: 'transparent',
                                          grid: { top: 16, right: 8, bottom: 38, left: 36, containLabel: false },
                                          xAxis: {
                                            type: 'category',
                                            data: xLabels,
                                            axisLabel: {
                                              color: '#6b7280',
                                              fontSize: 9,
                                              interval: labelInterval,
                                              formatter: (val, idx) => {
                                                // Only show label when year changes
                                                if (idx === 0 || xLabels[idx] !== xLabels[idx - 1]) return val;
                                                return '';
                                              },
                                            },
                                            axisLine: { lineStyle: { color: '#374151' } },
                                            axisTick: { show: false },
                                          },
                                          yAxis: {
                                            type: 'value',
                                            axisLabel: { color: '#6b7280', fontSize: 9 },
                                            splitLine: { lineStyle: { color: '#1f2937', type: 'dashed' } },
                                            axisLine: { show: false },
                                          },
                                          series: [
                                            {
                                              name: 'P/E',
                                              type: 'line',
                                              data: displayTs.map(d => d.pe),
                                              smooth: true,
                                              symbol: 'none',
                                              lineStyle: { color: '#60a5fa', width: 1.5 },
                                              areaStyle: { color: { type: 'linear', x: 0, y: 0, x2: 0, y2: 1, colorStops: [{ offset: 0, color: 'rgba(96,165,250,0.15)' }, { offset: 1, color: 'rgba(96,165,250,0)' }] } },
                                            },
                                            {
                                              name: `Avg (${peChartRange})`,
                                              type: 'line',
                                              data: displayTs.map(() => periodAvg),
                                              symbol: 'none',
                                              lineStyle: { color: '#9ca3af', width: 1, type: 'dashed' },
                                            },
                                          ],
                                          tooltip: {
                                            trigger: 'axis',
                                            backgroundColor: '#111827',
                                            borderColor: '#374151',
                                            textStyle: { color: '#e5e7eb', fontSize: 11 },
                                            formatter: (params) => {
                                              const date = params[0]?.axisValue || '';
                                              const pe = params[0]?.value != null ? `P/E: <b>${params[0].value}</b>` : '';
                                              const avg = params[1]?.value != null ? `${peChartRange} Avg: ${params[1].value}` : '';
                                              return `${date}<br/>${pe}${avg ? '<br/>' + avg : ''}`;
                                            },
                                          },
                                        }}
                                      />
                                      <div className="flex gap-4 mt-1 justify-center">
                                        <span className="text-[10px] text-gray-500">{peChartRange} Avg: <span className="text-gray-300 font-semibold">{periodAvg ?? 'N/A'}</span></span>
                                        {companyMetrics?.pe != null && <span className="text-[10px] text-gray-500">Current: <span className="text-blue-400 font-semibold">{formatNumber(companyMetrics.pe)}</span></span>}
                                      </div>
                                    </>
                                  );
                                })()}
                              </div>

                              {/* Columns 2 & 3: Industry and Peer scatter charts */}
                              {(() => {
                                // Y-only winsorize: filters solely on net_profit_cagr (Y-axis).
                                // This keeps the same peer set across P/E and P/B industry charts —
                                // peers are selected by CAGR outlier removal, not by valuation ratio.
                                const winsorizeY = (data, field, lo = 0.1, hi = 0.9) => {
                                  const vals = data.map(d => d[field]).filter(v => v != null).sort((a, b) => a - b);
                                  if (vals.length < 4) return data;
                                  const loVal = vals[Math.max(0, Math.floor(vals.length * lo))];
                                  const hiVal = vals[Math.max(0, Math.floor(vals.length * hi))];
                                  return data.filter(d => d[field] != null && d[field] >= loVal && d[field] <= hiVal);
                                };

                                // ── Industry Scatter: unchanged ──────────────────────────────────
                                const makeScatterOption = (rawScatter, label, skipWinsorize = false) => {
                                  // Winsorize ALL points (peers + target) at 0.1/0.9 so outliers
                                  // don't stretch the axis. Target excluded if it's extreme too.
                                  const withCagr = rawScatter.filter(d => d.net_profit_cagr != null);
                                  const allWinsorized = skipWinsorize ? withCagr : winsorizeY(withCagr, 'net_profit_cagr');
                                  const clean = allWinsorized.filter(d => !d.is_target);
                                  const target = allWinsorized.find(d => d.is_target);
                                  // Industry average P/E across visible peers
                                  const peVals = clean.map(d => d.pe).filter(v => v != null);
                                  const avgPe = peVals.length ? peVals.reduce((a, b) => a + b, 0) / peVals.length : null;
                                  return {
                                    backgroundColor: 'transparent',
                                    legend: {
                                      show: avgPe != null,
                                      top: 2, right: 4,
                                      data: avgPe != null ? [{ name: `Ind Avg P/E: ${avgPe.toFixed(1)}`, icon: 'none' }] : [],
                                      textStyle: { color: '#fbbf24', fontSize: 11, fontWeight: '600', fontFamily: 'Inter, system-ui, sans-serif' },
                                      itemWidth: 18, itemHeight: 2,
                                      selectedMode: false,
                                    },
                                    grid: { top: 28, right: 16, bottom: 38, left: 52, containLabel: false, borderColor: '#9ca3af', borderWidth: 1.5 },
                                    xAxis: {
                                      type: 'value', name: 'Price to Earnings (P/E)', nameLocation: 'middle', nameGap: 24,
                                      nameTextStyle: { color: '#e2e8f0', fontSize: 11, fontWeight: '500' },
                                      axisLabel: { color: '#d1d5db', fontSize: 10, formatter: v => Math.round(v) },
                                      splitLine: { lineStyle: { color: '#1f2937', type: 'dashed' } },
                                      axisLine: { show: true, onZero: false, lineStyle: { color: '#9ca3af', width: 1.5 } },
                                    },
                                    yAxis: {
                                      type: 'value', name: 'Net Profit CAGR %', nameLocation: 'middle', nameGap: 40,
                                      nameTextStyle: { color: '#e2e8f0', fontSize: 11, fontWeight: '500' },
                                      axisLabel: { color: '#d1d5db', fontSize: 9, formatter: v => `${v}%` },
                                      splitLine: { lineStyle: { color: '#1f2937', type: 'dashed' } },
                                      axisLine: { show: false },
                                    },
                                    series: [
                                      {
                                        name: label, type: 'scatter',
                                        data: clean.map(d => ({ value: [d.pe, d.net_profit_cagr], name: d.name, ticker: d.ticker })),
                                        markLine: {
                                          silent: true, animation: false, symbol: ['none', 'none'], label: { show: false },
                                          data: [
                                            ...(avgPe != null ? [{ xAxis: avgPe, lineStyle: { type: 'dashed', color: '#f59e0b', width: 1.5, opacity: 0.8 } }] : []),
                                            { yAxis: 0, lineStyle: { type: 'solid', color: '#4b5563', width: 1 } },
                                          ],
                                        },
                                        symbolSize: 8,
                                        itemStyle: { color: '#22c55e', borderColor: '#4ade80', borderWidth: 1.5 },
                                        label: { show: true, formatter: p => p.data.ticker || '', position: 'right', fontSize: 8, color: '#86efac', distance: 3 },
                                        labelLayout: { hideOverlap: true },
                                      },
                                      ...(avgPe != null ? [{ name: `Ind Avg P/E: ${avgPe.toFixed(1)}`, type: 'line', data: [], symbol: 'none', lineStyle: { type: 'dashed', color: '#f59e0b', width: 1.5 } }] : []),
                                      ...(target ? [{
                                        name: 'You', type: 'scatter',
                                        data: [{ value: [target.pe, target.net_profit_cagr], name: target.name, ticker: target.ticker }],
                                        symbolSize: 14, itemStyle: { color: '#3b82f6', borderColor: '#60a5fa', borderWidth: 1.5, opacity: 1 }, zlevel: 10,
                                        label: { show: true, formatter: p => p.data.ticker || '', position: 'top', fontSize: 9, color: '#60a5fa', fontWeight: 'bold', distance: 4 },
                                      }] : []),
                                    ],
                                    tooltip: {
                                      trigger: 'item', backgroundColor: '#111827', borderColor: '#374151',
                                      textStyle: { color: '#e5e7eb', fontSize: 11 },
                                      formatter: p => {
                                        const cagr = p.data.value[1];
                                        return `<b>${p.data.name}</b>${p.data.ticker ? ` (${p.data.ticker})` : ''}<br/>P/E: <b>${p.data.value[0]}</b><br/>NP CAGR: <b>${cagr != null ? cagr + '%' : 'N/A'}</b>`;
                                      },
                                    },
                                  };
                                };

                                // ── Peer Scatter: clean rewrite ─────────────────────────────────
                                // Reads peer_winsorize flag from backend to decide whether to winsorize.
                                // Color: green = lower P/E than target, orange = higher P/E than target.
                                const makePeerScatterOption = (rawScatter, label, shouldWinsorize) => {
                                  // Use Y=ROE; filter to points with ROE data, winsorize on ROE
                                  const nonTarget = rawScatter.filter(d => !d.is_target && d.roe != null);
                                  const target = rawScatter.find(d => d.is_target);
                                  const clean = shouldWinsorize ? winsorizeY(nonTarget, 'roe') : nonTarget;
                                  const roeVals = clean.map(d => d.roe).filter(v => v != null);
                                  const avgRoe = roeVals.length ? roeVals.reduce((a, b) => a + b, 0) / roeVals.length : null;

                                  return {
                                    backgroundColor: 'transparent',
                                    legend: {
                                      show: avgRoe != null,
                                      top: 2, right: 4,
                                      data: avgRoe != null ? [{ name: `Avg ROE: ${avgRoe.toFixed(1)}%`, icon: 'none' }] : [],
                                      textStyle: { color: '#fbbf24', fontSize: 11, fontWeight: '600', fontFamily: 'Inter, system-ui, sans-serif' },
                                      itemWidth: 18, itemHeight: 2, selectedMode: false,
                                    },
                                    grid: { top: 28, right: 16, bottom: 38, left: 52, containLabel: false, borderColor: '#9ca3af', borderWidth: 1.5 },
                                    xAxis: {
                                      type: 'value', name: 'Price to Earnings (P/E)', nameLocation: 'middle', nameGap: 24,
                                      nameTextStyle: { color: '#e2e8f0', fontSize: 11, fontWeight: '500' },
                                      axisLabel: { color: '#d1d5db', fontSize: 10, formatter: v => Math.round(v) },
                                      splitLine: { lineStyle: { color: '#1f2937', type: 'dashed' } },
                                      axisLine: { show: true, onZero: false, lineStyle: { color: '#9ca3af', width: 1.5 } },
                                    },
                                    yAxis: {
                                      type: 'value', name: 'Return on Equity %', nameLocation: 'middle', nameGap: 40,
                                      nameTextStyle: { color: '#e2e8f0', fontSize: 11, fontWeight: '500' },
                                      axisLabel: { color: '#d1d5db', fontSize: 9, formatter: v => `${v}%` },
                                      splitLine: { lineStyle: { color: '#1f2937', type: 'dashed' } },
                                      axisLine: { show: false },
                                    },
                                    series: [
                                      {
                                        name: 'Peers', type: 'scatter',
                                        data: clean.map(d => ({ value: [d.pe, d.roe], name: d.name, ticker: d.ticker })),
                                        symbolSize: 8,
                                        itemStyle: { color: '#22c55e', borderColor: '#4ade80', borderWidth: 1.5 },
                                        label: { show: true, formatter: p => p.data.ticker || '', position: 'right', fontSize: 8, color: '#86efac', distance: 3 },
                                        labelLayout: { hideOverlap: true },
                                        markLine: avgRoe != null ? { silent: true, animation: false, symbol: ['none', 'none'], lineStyle: { type: 'dashed', color: '#f59e0b', width: 1.5, opacity: 0.8 }, label: { show: false }, data: [{ yAxis: avgRoe }] } : undefined,
                                      },
                                      ...(avgRoe != null ? [{ name: `Avg ROE: ${avgRoe.toFixed(1)}%`, type: 'line', data: [], symbol: 'none', lineStyle: { type: 'dashed', color: '#f59e0b', width: 1.5 } }] : []),
                                      ...(target ? [{
                                        name: 'You', type: 'scatter',
                                        data: [{ value: [target.pe, target.roe ?? 0], name: target.name, ticker: target.ticker }],
                                        symbolSize: 12,
                                        itemStyle: { color: '#3b82f6', borderColor: '#60a5fa', borderWidth: 2, opacity: 1 },
                                        zlevel: 10,
                                        label: { show: true, formatter: p => p.data.ticker || '', position: 'top', fontSize: 9, color: '#60a5fa', fontWeight: 'bold', distance: 4 },
                                      }] : []),
                                    ],
                                    tooltip: {
                                      trigger: 'item', backgroundColor: '#111827', borderColor: '#374151',
                                      textStyle: { color: '#e5e7eb', fontSize: 11 },
                                      formatter: p => `<b>${p.data.name}</b>${p.data.ticker ? ` (${p.data.ticker})` : ''}<br/>P/E: <b>${p.data.value[0]}</b><br/>ROE: <b>${p.data.value[1] != null ? p.data.value[1] + '%' : 'N/A'}</b>`,
                                    },
                                  };
                                };

                                const indScatter = growthMetrics.valuation_history?.industry_scatter || [];
                                const peerScatter = growthMetrics.valuation_history?.peer_scatter || [];
                                const peerWinsorize = growthMetrics.valuation_history?.peer_winsorize ?? true;
                                const peerLabel = 'Market Cap (Peers)';

                                return (
                                  <>
                                    {indScatter.length > 0 && (
                                      <div className="flex-1 min-w-0">
                                        <div className="flex items-center justify-between mb-2">
                                          <span className="text-gray-100 text-xs font-semibold tracking-wider">Industry (Peers)</span>
                                          <button onClick={() => setScatterModal({ title: 'P/E — Industry (SIC)', option: makeScatterOption(indScatter, 'Industry', false) })} className="text-gray-500 hover:text-gray-200 transition-colors p-0.5 rounded" title="Expand"><svg width="13" height="13" viewBox="0 0 13 13" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="8,1 12,1 12,5" /><polyline points="5,12 1,12 1,8" /><line x1="12" y1="1" x2="7.5" y2="5.5" /><line x1="1" y1="12" x2="5.5" y2="7.5" /></svg></button>
                                        </div>
                                        <ReactECharts style={{ height: '220px', width: '100%' }} option={makeScatterOption(indScatter, 'Industry', false)} />
                                      </div>
                                    )}
                                    {peerScatter.length > 0 && (
                                      <div className="flex-1 min-w-0">
                                        <div className="flex items-center justify-between mb-2">
                                          <span className="text-gray-100 text-xs font-semibold tracking-wider">{peerLabel}</span>
                                          <button onClick={() => setScatterModal({ title: `P/E — ${peerLabel}`, option: makePeerScatterOption(peerScatter, 'Peers', peerWinsorize) })} className="text-gray-500 hover:text-gray-200 transition-colors p-0.5 rounded" title="Expand"><svg width="13" height="13" viewBox="0 0 13 13" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="8,1 12,1 12,5" /><polyline points="5,12 1,12 1,8" /><line x1="12" y1="1" x2="7.5" y2="5.5" /><line x1="1" y1="12" x2="5.5" y2="7.5" /></svg></button>
                                        </div>
                                        <ReactECharts style={{ height: '220px', width: '100%' }} option={makePeerScatterOption(peerScatter, 'Peers', peerWinsorize)} />
                                      </div>
                                    )}
                                  </>
                                );
                              })()}
                            </div>
                          </div>

                          {/* Price to Book (P/B) Panel */}
                          <div className="bg-gray-800 border border-gray-700 rounded-lg px-5 py-5">
                            <h3 className="text-base font-semibold mb-6 text-white">Price to Book (P/B)</h3>
                            <div className="flex gap-4">

                              {/* Column 1: Historical P/B line chart */}
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 mb-2">
                                  <span className="text-gray-100 text-xs font-semibold tracking-wider">Historical P/B</span>
                                  {growthMetrics.valuation_history?.pb?.timeseries?.length > 0 && (
                                    <div className="flex items-center gap-0.5 bg-gray-900/60 rounded p-0.5">
                                      {["3Y", "5Y", "10Y"].map(r => (
                                        <button
                                          key={r}
                                          onClick={() => setPbChartRange(r)}
                                          className={`px-1.5 py-0.5 text-[9px] font-semibold rounded transition-colors ${pbChartRange === r ? 'bg-blue-600 text-white' : 'text-gray-400 hover:text-gray-200'}`}
                                        >
                                          {r}
                                        </button>
                                      ))}
                                    </div>
                                  )}
                                </div>
                                {(() => {
                                  const allTs = growthMetrics.valuation_history?.pb?.timeseries || [];
                                  if (allTs.length === 0) return <p className="text-sm font-bold text-gray-400">N/A</p>;
                                  const yearsBack = pbChartRange === "3Y" ? 3 : pbChartRange === "5Y" ? 5 : 10;
                                  const cutoff = new Date();
                                  cutoff.setFullYear(cutoff.getFullYear() - yearsBack);
                                  const cutoffStr = `${cutoff.getFullYear()}-${String(cutoff.getMonth() + 1).padStart(2, '0')}`;
                                  const filtered = allTs.filter(d => d.date >= cutoffStr);
                                  const displayTs = filtered.length > 0 ? filtered : allTs;
                                  const validPb = displayTs.filter(d => d.pb != null).map(d => d.pb);
                                  const periodAvg = validPb.length > 0 ? Math.round((validPb.reduce((a, b) => a + b, 0) / validPb.length) * 10) / 10 : null;
                                  const xLabels = displayTs.map(d => d.date.substring(0, 4));
                                  const labelInterval = Math.max(0, Math.floor(displayTs.length / 5) - 1);
                                  return (
                                    <>
                                      <ReactECharts
                                        style={{ height: '220px', width: '100%' }}
                                        option={{
                                          backgroundColor: 'transparent',
                                          grid: { top: 16, right: 8, bottom: 38, left: 36, containLabel: false },
                                          xAxis: {
                                            type: 'category',
                                            data: xLabels,
                                            axisLabel: {
                                              color: '#6b7280', fontSize: 9,
                                              interval: labelInterval,
                                              formatter: (val, idx) => {
                                                if (idx === 0 || xLabels[idx] !== xLabels[idx - 1]) return val;
                                                return '';
                                              },
                                            },
                                            axisLine: { lineStyle: { color: '#374151' } },
                                            axisTick: { show: false },
                                          },
                                          yAxis: {
                                            type: 'value',
                                            axisLabel: { color: '#6b7280', fontSize: 9 },
                                            splitLine: { lineStyle: { color: '#1f2937', type: 'dashed' } },
                                            axisLine: { show: false },
                                          },
                                          series: [
                                            {
                                              name: 'P/B',
                                              type: 'line',
                                              data: displayTs.map(d => d.pb),
                                              smooth: true,
                                              symbol: 'none',
                                              lineStyle: { color: '#a78bfa', width: 1.5 },
                                              areaStyle: { color: { type: 'linear', x: 0, y: 0, x2: 0, y2: 1, colorStops: [{ offset: 0, color: 'rgba(167,139,250,0.15)' }, { offset: 1, color: 'rgba(167,139,250,0)' }] } },
                                            },
                                            {
                                              name: `Avg (${pbChartRange})`,
                                              type: 'line',
                                              data: displayTs.map(() => periodAvg),
                                              symbol: 'none',
                                              lineStyle: { color: '#9ca3af', width: 1, type: 'dashed' },
                                            },
                                          ],
                                          tooltip: {
                                            trigger: 'axis',
                                            backgroundColor: '#111827',
                                            borderColor: '#374151',
                                            textStyle: { color: '#e5e7eb', fontSize: 11 },
                                            formatter: (params) => {
                                              const date = params[0]?.axisValue || '';
                                              const pb = params[0]?.value != null ? `P/B: <b>${params[0].value}</b>` : '';
                                              const avg = params[1]?.value != null ? `${pbChartRange} Avg: ${params[1].value}` : '';
                                              return `${date}<br/>${pb}${avg ? '<br/>' + avg : ''}`;
                                            },
                                          },
                                        }}
                                      />
                                      <div className="flex gap-4 mt-1 justify-center">
                                        <span className="text-[10px] text-gray-500">{pbChartRange} Avg: <span className="text-gray-300 font-semibold">{periodAvg ?? 'N/A'}</span></span>
                                        {companyMetrics?.price_to_book != null && <span className="text-[10px] text-gray-500">Current: <span className="text-purple-400 font-semibold">{formatNumber(companyMetrics.price_to_book)}</span></span>}
                                      </div>
                                    </>
                                  );
                                })()}
                              </div>

                              {/* Columns 2 & 3: P/B Industry and Peer scatter charts */}
                              {(() => {
                                const winsorizeY = (data, field, lo = 0.1, hi = 0.9) => {
                                  const vals = data.map(d => d[field]).filter(v => v != null).sort((a, b) => a - b);
                                  if (vals.length < 4) return data;
                                  const loVal = vals[Math.max(0, Math.floor(vals.length * lo))];
                                  const hiVal = vals[Math.max(0, Math.floor(vals.length * hi))];
                                  return data.filter(d => d[field] != null && d[field] >= loVal && d[field] <= hiVal);
                                };

                                const makePbScatterOption = (rawScatter, label, skipWinsorize, isPeer) => {
                                  // INDUSTRY chart: winsorize ALL points (peers + target) at 0.1/0.9
                                  if (!isPeer) {
                                    const withCagr = rawScatter.filter(d => d.net_profit_cagr != null);
                                    const allWinsorized = skipWinsorize ? withCagr : winsorizeY(withCagr, 'net_profit_cagr');
                                    const cleanInd = allWinsorized.filter(d => !d.is_target);
                                    const targetInd = allWinsorized.find(d => d.is_target);
                                    // Industry average P/B across visible peers
                                    const pbVals = cleanInd.map(d => d.pb).filter(v => v != null);
                                    const avgPb = pbVals.length ? pbVals.reduce((a, b) => a + b, 0) / pbVals.length : null;
                                    return {
                                      backgroundColor: 'transparent',
                                      legend: {
                                        show: avgPb != null,
                                        top: 2, right: 4,
                                        data: avgPb != null ? [{ name: `Ind Avg P/B: ${avgPb.toFixed(1)}`, icon: 'none' }] : [],
                                        textStyle: { color: '#fbbf24', fontSize: 11, fontWeight: '600', fontFamily: 'Inter, system-ui, sans-serif' },
                                        itemWidth: 18, itemHeight: 2,
                                        selectedMode: false,
                                      },
                                      grid: { top: 28, right: 16, bottom: 38, left: 52, containLabel: false, borderColor: '#9ca3af', borderWidth: 1.5 },
                                      xAxis: {
                                        type: 'value', name: 'Price to Book (P/B)', nameLocation: 'middle', nameGap: 24,
                                        nameTextStyle: { color: '#e2e8f0', fontSize: 11, fontWeight: '500' },
                                        axisLabel: { color: '#d1d5db', fontSize: 10, formatter: v => Math.round(v * 10) / 10 },
                                        splitLine: { lineStyle: { color: '#1f2937', type: 'dashed' } },
                                        axisLine: { show: true, onZero: false, lineStyle: { color: '#9ca3af', width: 1.5 } },
                                      },
                                      yAxis: {
                                        type: 'value', name: 'Net Profit CAGR %', nameLocation: 'middle', nameGap: 40,
                                        nameTextStyle: { color: '#e2e8f0', fontSize: 11, fontWeight: '500' },
                                        axisLabel: { color: '#d1d5db', fontSize: 10, formatter: v => `${v}%` },
                                        splitLine: { lineStyle: { color: '#1f2937', type: 'dashed' } },
                                        axisLine: { show: false },
                                      },
                                      series: [
                                        {
                                          name: label, type: 'scatter',
                                          data: cleanInd.map(d => ({ value: [d.pb, d.net_profit_cagr], name: d.name, ticker: d.ticker })),
                                          markLine: {
                                            silent: true, animation: false, symbol: ['none', 'none'], label: { show: false },
                                            data: [
                                              ...(avgPb != null ? [{ xAxis: avgPb, lineStyle: { type: 'dashed', color: '#f59e0b', width: 1.5, opacity: 0.8 } }] : []),
                                              { yAxis: 0, lineStyle: { type: 'solid', color: '#4b5563', width: 1 } },
                                            ],
                                          },
                                          symbolSize: 8, itemStyle: { color: '#a855f7', borderColor: '#c084fc', borderWidth: 1.5 },
                                          label: { show: true, formatter: p => p.data.ticker || '', position: 'right', fontSize: 8, color: '#d8b4fe', distance: 3 },
                                          labelLayout: { hideOverlap: true }
                                        },
                                        ...(avgPb != null ? [{ name: `Ind Avg P/B: ${avgPb.toFixed(1)}`, type: 'line', data: [], symbol: 'none', lineStyle: { type: 'dashed', color: '#f59e0b', width: 1.5 } }] : []),
                                        ...(targetInd ? [{
                                          name: 'You', type: 'scatter',
                                          data: [{ value: [targetInd.pb, targetInd.net_profit_cagr], name: targetInd.name, ticker: targetInd.ticker }],
                                          symbolSize: 14, itemStyle: { color: '#3b82f6', borderColor: '#60a5fa', borderWidth: 2, opacity: 1 }, zlevel: 10,
                                          label: { show: true, formatter: p => p.data.ticker || '', position: 'top', fontSize: 9, color: '#60a5fa', fontWeight: 'bold', distance: 4 },
                                        }] : []),
                                      ],
                                      tooltip: {
                                        trigger: 'item', backgroundColor: '#111827', borderColor: '#374151', textStyle: { color: '#e5e7eb', fontSize: 11 },
                                        formatter: p => `<b>${p.data.name}</b>${p.data.ticker ? ` (${p.data.ticker})` : ''}<br/>P/B: <b>${p.data.value[0]}</b><br/>NP CAGR: <b>${p.data.value[1] != null ? p.data.value[1] + '%' : 'N/A'}</b>`
                                      },
                                    };
                                  }

                                  // PEERS chart: X=P/B, Y=ROA — winsorize on ROA, target added separately
                                  const nonTarget = rawScatter.filter(d => !d.is_target && d.rote != null);
                                  const target = rawScatter.find(d => d.is_target);
                                  const clean = skipWinsorize ? nonTarget : winsorizeY(nonTarget, 'rote');
                                  const roteVals = clean.map(d => d.rote).filter(v => v != null);
                                  const avgRote = roteVals.length ? roteVals.reduce((a, b) => a + b, 0) / roteVals.length : null;
                                  return {
                                    backgroundColor: 'transparent',
                                    legend: {
                                      show: avgRote != null,
                                      top: 2, right: 4,
                                      data: avgRote != null ? [{ name: `Avg ROTE: ${avgRote.toFixed(1)}%`, icon: 'none' }] : [],
                                      textStyle: { color: '#fbbf24', fontSize: 11, fontWeight: '600', fontFamily: 'Inter, system-ui, sans-serif' },
                                      itemWidth: 18, itemHeight: 2, selectedMode: false,
                                    },
                                    grid: { top: 28, right: 16, bottom: 38, left: 52, containLabel: false, borderColor: '#9ca3af', borderWidth: 1.5 },
                                    xAxis: {
                                      type: 'value', name: 'Price to Book (P/B)', nameLocation: 'middle', nameGap: 24,
                                      nameTextStyle: { color: '#e2e8f0', fontSize: 11, fontWeight: '500' },
                                      axisLabel: { color: '#d1d5db', fontSize: 10, formatter: v => Math.round(v * 10) / 10 },
                                      splitLine: { lineStyle: { color: '#1f2937', type: 'dashed' } },
                                      axisLine: { show: true, onZero: false, lineStyle: { color: '#9ca3af', width: 1.5 } },
                                    },
                                    yAxis: {
                                      type: 'value', name: 'Return on Tangible Equity %', nameLocation: 'middle', nameGap: 40,
                                      nameTextStyle: { color: '#e2e8f0', fontSize: 11, fontWeight: '500' },
                                      axisLabel: { color: '#d1d5db', fontSize: 9, formatter: v => `${v}%` },
                                      splitLine: { lineStyle: { color: '#1f2937', type: 'dashed' } },
                                      axisLine: { show: false },
                                    },
                                    series: [
                                      {
                                        name: 'Peers', type: 'scatter',
                                        data: clean.map(d => ({ value: [d.pb, d.rote], name: d.name, ticker: d.ticker })),
                                        symbolSize: 8, itemStyle: { color: '#a855f7', borderColor: '#c084fc', borderWidth: 1.5 },
                                        label: { show: true, formatter: p => p.data.ticker || '', position: 'right', fontSize: 8, color: '#d8b4fe', distance: 3 },
                                        labelLayout: { hideOverlap: true },
                                        markLine: avgRote != null ? { silent: true, animation: false, symbol: ['none', 'none'], lineStyle: { type: 'dashed', color: '#f59e0b', width: 1.5, opacity: 0.8 }, label: { show: false }, data: [{ yAxis: avgRote }] } : undefined
                                      },
                                      ...(avgRote != null ? [{ name: `Avg ROTE: ${avgRote.toFixed(1)}%`, type: 'line', data: [], symbol: 'none', lineStyle: { type: 'dashed', color: '#f59e0b', width: 1.5 } }] : []),
                                      ...(target ? [{
                                        name: 'You', type: 'scatter',
                                        data: [{ value: [target.pb, target.rote ?? 0], name: target.name, ticker: target.ticker }],
                                        symbolSize: 14, itemStyle: { color: '#3b82f6', borderColor: '#60a5fa', borderWidth: 2, opacity: 1 }, zlevel: 10,
                                        label: { show: true, formatter: p => p.data.ticker || '', position: 'top', fontSize: 9, color: '#60a5fa', fontWeight: 'bold', distance: 4 },
                                      }] : []),
                                    ],
                                    tooltip: {
                                      trigger: 'item', backgroundColor: '#111827', borderColor: '#374151', textStyle: { color: '#e5e7eb', fontSize: 11 },
                                      formatter: p => `<b>${p.data.name}</b>${p.data.ticker ? ` (${p.data.ticker})` : ''}<br/>P/B: <b>${p.data.value[0]}</b><br/>ROTE: <b>${p.data.value[1] != null ? p.data.value[1] + '%' : 'N/A'}</b>`
                                    },
                                  };
                                };

                                const indPbScatter = growthMetrics.valuation_history?.industry_pb_scatter || [];
                                const peerPbScatter = growthMetrics.valuation_history?.peer_pb_scatter || [];
                                const peerPbWinsorize = growthMetrics.valuation_history?.peer_pb_winsorize ?? true;

                                const peerPbLabel = 'Market Cap (Peers)';

                                return (
                                  <>
                                    {indPbScatter.length > 0 && (
                                      <div className="flex-1 min-w-0">
                                        <div className="flex items-center justify-between mb-2">
                                          <span className="text-gray-100 text-xs font-semibold tracking-wider">Industry (Peers)</span>
                                          <button onClick={() => setScatterModal({ title: 'P/B — Industry (SIC)', option: makePbScatterOption(indPbScatter, 'Industry', false, false) })} className="text-gray-500 hover:text-gray-200 transition-colors p-0.5 rounded" title="Expand"><svg width="13" height="13" viewBox="0 0 13 13" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="8,1 12,1 12,5" /><polyline points="5,12 1,12 1,8" /><line x1="12" y1="1" x2="7.5" y2="5.5" /><line x1="1" y1="12" x2="5.5" y2="7.5" /></svg></button>
                                        </div>
                                        <ReactECharts style={{ height: '220px', width: '100%' }} option={makePbScatterOption(indPbScatter, 'Industry', false, false)} />
                                      </div>
                                    )}
                                    {peerPbScatter.length > 0 && (
                                      <div className="flex-1 min-w-0">
                                        <div className="flex items-center justify-between mb-2">
                                          <span className="text-gray-100 text-xs font-semibold tracking-wider">{peerPbLabel}</span>
                                          <button onClick={() => setScatterModal({ title: `P/B — ${peerPbLabel}`, option: makePbScatterOption(peerPbScatter, 'Peers', !peerPbWinsorize, true) })} className="text-gray-500 hover:text-gray-200 transition-colors p-0.5 rounded" title="Expand"><svg width="13" height="13" viewBox="0 0 13 13" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="8,1 12,1 12,5" /><polyline points="5,12 1,12 1,8" /><line x1="12" y1="1" x2="7.5" y2="5.5" /><line x1="1" y1="12" x2="5.5" y2="7.5" /></svg></button>
                                        </div>
                                        <ReactECharts style={{ height: '220px', width: '100%' }} option={makePbScatterOption(peerPbScatter, 'Peers', !peerPbWinsorize, true)} />
                                      </div>
                                    )}
                                  </>
                                );
                              })()}
                            </div>
                          </div>
                        </>
                      )}

                      {!growthMetricsLoading && !growthMetricsError && !growthMetrics && (
                        <p className="text-gray-500 text-sm">Growth metrics unavailable.</p>
                      )}
                    </div>
                  )}

                  {/* ── End Valuation Tab ─────────────────────────── */}
                </>
              )}

              {/* ── Management Discussion Tab Content ────────── */}
              {mainTab === "mdna" && selectedCompany && (
                <div>
                  {/* Filing Info Bar */}
                  {mdnaFilingInfo && (
                    <div className="flex flex-wrap gap-3 mb-4">
                      {[
                        { label: "Form", value: mdnaFilingInfo.form },
                        { label: "Filed", value: mdnaFilingInfo.filing_date },
                      ].map((chip) => (
                        <span key={chip.label} className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-gray-800 border border-gray-700 rounded-md text-xs">
                          <span className="text-gray-500">{chip.label}:</span>
                          <span className="text-blue-400 font-medium">{chip.value}</span>
                        </span>
                      ))}
                      {mdnaFilingInfo.filing_url && (
                        <a href={mdnaFilingInfo.filing_url} target="_blank" rel="noreferrer"
                          className="inline-flex items-center gap-1 px-3 py-1.5 bg-gray-800 border border-gray-700 rounded-md text-xs text-indigo-400 hover:text-indigo-300 hover:border-indigo-500/50 transition-colors">
                          View Full Filing ↗
                        </a>
                      )}
                    </div>
                  )}

                  {/* AI Summary Section (always visible) */}
                  <div className="bg-gray-800 border border-gray-700 rounded-lg p-5 mb-4">
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="text-base font-semibold text-gray-100 flex items-center gap-2">
                        <span className="text-lg">✨</span> Management Discussion Summary
                      </h3>
                      {mdnaSummary && (
                        <button
                          onClick={() => {
                            if (isSpeaking) {
                              window.speechSynthesis.cancel();
                              setIsSpeaking(false);
                            } else {
                              const utterance = new SpeechSynthesisUtterance(mdnaSummary);
                              if (selectedVoiceRef.current) utterance.voice = selectedVoiceRef.current;
                              utterance.rate = 1.05;
                              utterance.pitch = 1.0;
                              utterance.onend = () => setIsSpeaking(false);
                              utterance.onerror = () => setIsSpeaking(false);
                              window.speechSynthesis.speak(utterance);
                              setIsSpeaking(true);
                            }
                          }}
                          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-all duration-200 ${isSpeaking
                            ? "bg-red-500/20 text-red-400 border border-red-500/30 hover:bg-red-500/30"
                            : "bg-blue-500/10 text-blue-400 border border-blue-500/20 hover:bg-blue-500/20"
                            }`}
                          title={isSpeaking ? "Stop reading" : "Read aloud"}
                        >
                          {isSpeaking ? (
                            <>
                              <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24">
                                <rect x="6" y="4" width="4" height="16" rx="1" />
                                <rect x="14" y="4" width="4" height="16" rx="1" />
                              </svg>
                              Stop
                            </>
                          ) : (
                            <>
                              <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24">
                                <path d="M11.383 3.07C11.009 2.89 10.579 2.905 10.219 3.11L4.934 6H2C0.897 6 0 6.897 0 8v8c0 1.103 0.897 2 2 2h2.934l5.285 2.89C10.404 21.038 10.606 21.1 10.809 21.1c0.163 0 0.326-0.04 0.474-0.12C11.633 20.784 12 20.426 12 20V4c0-0.426-0.367-0.784-0.617-0.93zM14 8.5c1.5 0.75 2.5 2.25 2.5 3.5s-1 2.75-2.5 3.5" stroke="currentColor" strokeWidth="1.5" fill="none" />
                              </svg>
                              Listen
                            </>
                          )}
                        </button>
                      )}
                    </div>
                    {mdnaSummaryLoading && (
                      <div className="flex items-center gap-3 py-4">
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-400"></div>
                        <LoadingText steps={[
                          "Checking database for previous summaries...",
                          "Scanning SEC EDGAR for new filings...",
                          "Extracting Management Discussion section...",
                          "Analyzing financial text with Gemini AI...",
                          "Formatting AI insights..."
                        ]} interval={1500} />
                      </div>
                    )}
                    {mdnaSummaryError && (
                      <div className="bg-red-900/30 border border-red-700/50 rounded-md p-3 text-sm text-red-300">
                        {mdnaSummaryError}
                      </div>
                    )}
                    {!mdnaSummaryLoading && !mdnaSummaryError && mdnaSummary && (
                      <TypewriterText text={mdnaSummary} speed={8} className="text-gray-300 text-sm leading-relaxed whitespace-pre-wrap" />
                    )}
                    {!mdnaSummaryLoading && !mdnaSummaryError && !mdnaSummary && (
                      <p className="text-gray-500 text-sm italic">Select a company to see the Management Discussion summary.</p>
                    )}

                    {!mdnaSummaryLoading && !mdnaSummaryError && mdnaSummary && (
                      <div className="mt-5 pt-4 border-t border-gray-700 flex justify-end">
                        <button
                          onClick={() => setIsMdnaFullTextModalOpen(true)}
                          className="text-sm font-medium text-blue-400 hover:text-blue-300 transition-colors flex items-center gap-1.5"
                        >
                          View Full Text
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                          </svg>
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Earnings Call Summary Section */}
                  <div className="bg-gray-800 border border-gray-700 rounded-lg p-5 mb-4">
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="text-base font-semibold text-gray-100 flex items-center gap-2">
                        <span className="text-lg">✨</span> Earnings Call Summary
                      </h3>
                      {earningsSummary && (
                        <button
                          onClick={() => {
                            if (isEarningsSpeaking) {
                              window.speechSynthesis.cancel();
                              setIsEarningsSpeaking(false);
                            } else {
                              // Strip markdown characters so speech synthesis sounds natural
                              const cleanText = earningsSummary.replace(/[#*_{}[\]()~`>+-]/g, " ");
                              const utterance = new SpeechSynthesisUtterance(cleanText);
                              if (selectedVoiceRef.current) utterance.voice = selectedVoiceRef.current;
                              utterance.rate = 1.05;
                              utterance.pitch = 1.0;
                              utterance.onend = () => setIsEarningsSpeaking(false);
                              utterance.onerror = () => setIsEarningsSpeaking(false);
                              window.speechSynthesis.speak(utterance);
                              setIsEarningsSpeaking(true);
                            }
                          }}
                          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-all duration-200 ${isEarningsSpeaking
                            ? "bg-red-500/20 text-red-400 border border-red-500/30 hover:bg-red-500/30"
                            : "bg-green-500/10 text-green-400 border border-green-500/20 hover:bg-green-500/20"
                            }`}
                          title={isEarningsSpeaking ? "Stop reading" : "Read aloud"}
                        >
                          {isEarningsSpeaking ? (
                            <>
                              <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24">
                                <rect x="6" y="4" width="4" height="16" rx="1" />
                                <rect x="14" y="4" width="4" height="16" rx="1" />
                              </svg>
                              Stop
                            </>
                          ) : (
                            <>
                              <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24">
                                <path d="M11.383 3.07C11.009 2.89 10.579 2.905 10.219 3.11L4.934 6H2C0.897 6 0 6.897 0 8v8c0 1.103 0.897 2 2 2h2.934l5.285 2.89C10.404 21.038 10.606 21.1 10.809 21.1c0.163 0 0.326-0.04 0.474-0.12C11.633 20.784 12 20.426 12 20V4c0-0.426-0.367-0.784-0.617-0.93zM14 8.5c1.5 0.75 2.5 2.25 2.5 3.5s-1 2.75-2.5 3.5" stroke="currentColor" strokeWidth="1.5" fill="none" />
                              </svg>
                              Listen
                            </>
                          )}
                        </button>
                      )}
                    </div>
                    {(earningsCallLoading || earningsSummaryLoading) && (
                      <div className="flex items-center gap-3 py-4">
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-green-400"></div>
                        <LoadingText steps={[
                          "Checking database for cached transcripts...",
                          "Fetching new earnings calls...",
                          "Parsing dialogue and speaker roles...",
                          "Generating strategic summary with Gemini...",
                          "Structuring analysis..."
                        ]} interval={1500} />
                      </div>
                    )}
                    {(earningsCallError || earningsSummaryError) && (
                      <div className="bg-red-900/30 border border-red-700/50 rounded-md p-3 text-sm text-red-300">
                        {earningsCallError || earningsSummaryError}
                      </div>
                    )}
                    {!earningsCallLoading && !earningsSummaryLoading && !earningsCallError && !earningsSummaryError && earningsSummary && (
                      <div className="text-gray-300 text-sm leading-relaxed mdna-content">
                        <TypewriterMarkdown text={earningsSummary} speed={8} />
                      </div>
                    )}
                    {!earningsCallLoading && !earningsSummaryLoading && !earningsCallError && !earningsSummaryError && !earningsSummary && (
                      <p className="text-gray-500 text-sm italic">Select a company to see the earnings call summary.</p>
                    )}

                    {!earningsCallLoading && !earningsCallError && earningsCallData && (
                      <div className="mt-5 pt-4 border-t border-gray-700 flex justify-end">
                        <button
                          onClick={() => setIsEarningsModalOpen(true)}
                          className="text-sm font-medium text-green-400 hover:text-green-300 transition-colors flex items-center gap-1.5"
                        >
                          View Full Transcript
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                          </svg>
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Earnings Call Modal */}
                  {isEarningsModalOpen && earningsCallData && (
                    <div className="fixed top-[64px] inset-x-0 bottom-0 z-[999] flex items-center justify-center bg-black/80 px-4 py-6" onClick={() => setIsEarningsModalOpen(false)}>
                      <div className="bg-gray-800 border border-gray-700 rounded-xl overflow-hidden shadow-2xl w-full max-w-5xl flex flex-col h-full" onClick={e => e.stopPropagation()}>
                        <div className="flex items-center justify-between p-5 border-b border-gray-700 bg-gray-900 sticky top-0 z-10 shrink-0">
                          <div>
                            <h3 className="text-lg font-semibold text-gray-100">
                              Earnings Call - Full Transcript
                            </h3>
                            <div className="text-sm text-gray-500 mt-1">{earningsCallData.title}</div>
                          </div>
                          <button
                            onClick={() => setIsEarningsModalOpen(false)}
                            className="p-1.5 text-gray-400 hover:text-gray-200 hover:bg-gray-700 rounded-md transition-colors"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                          </button>
                        </div>
                        <div className="p-6 overflow-y-auto space-y-6">
                          {earningsCallData.dialogs && earningsCallData.dialogs.map((dg, idx) => (
                            <div key={idx} className="bg-gray-800 border border-gray-700 p-4 rounded-lg flex flex-col gap-2">
                              <div className="flex items-center justify-between pb-2 border-b border-gray-700/50">
                                <div className="flex items-baseline gap-2">
                                  <span className="font-semibold text-green-400 text-base">{dg.name}</span>
                                  {dg.role && <span className="text-sm text-gray-500 italic">{dg.role}</span>}
                                </div>
                                {dg.time && <span className="text-xs font-mono text-gray-500">{dg.time}</span>}
                              </div>
                              <p className="text-gray-300 text-sm whitespace-pre-wrap leading-relaxed">{dg.text}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Full Text Modal */}
                  {isMdnaFullTextModalOpen && (
                    <div className="fixed top-[64px] inset-x-0 bottom-0 z-[999] flex items-center justify-center bg-black/80 px-4 py-6" onClick={() => setIsMdnaFullTextModalOpen(false)}>
                      <div className="bg-gray-800 border border-gray-700 rounded-xl overflow-hidden shadow-2xl w-full max-w-5xl flex flex-col h-full" onClick={e => e.stopPropagation()}>
                        <div className="flex items-center justify-between p-5 border-b border-gray-700 bg-gray-900 sticky top-0 z-10 shrink-0">
                          <h3 className="text-lg font-semibold text-gray-100">
                            Management Discussion and Analysis - Full Text
                          </h3>
                          <button
                            onClick={() => setIsMdnaFullTextModalOpen(false)}
                            className="p-1.5 text-gray-400 hover:text-gray-200 hover:bg-gray-700 rounded-md transition-colors"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                          </button>
                        </div>
                        <div className="p-6 overflow-y-auto">
                          {mdnaLoading && (
                            <div className="flex items-center gap-3 py-8 justify-center">
                              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-400"></div>
                              <span className="text-gray-400 text-sm">Fetching MD&A from SEC EDGAR...</span>
                            </div>
                          )}
                          {mdnaError && (
                            <div className="bg-red-900/30 border border-red-700/50 rounded-md p-3 text-sm text-red-300">{mdnaError}</div>
                          )}
                          {!mdnaLoading && !mdnaError && (mdnaMarkdown || mdnaText) && (
                            <div className="mdna-content text-gray-300 text-sm leading-7 max-w-none">
                              {(() => {
                                let content = mdnaMarkdown || mdnaText || '';
                                // Strip out SEC boilerplate headers (e.g., Item 7, Disclaimers) that might be in cached markdown
                                content = content.replace(/^##\s*(?:Item\s*[27][.\s]*|Part\s*I{1,2}[.\s]*|Management['’]s\s*Discussion\s*and\s*Analysis.*|Results\s*of\s*Operations.*|Cautionary.*|Forward-looking.*|Safe\s*Harbor.*)/gim, '');
                                // Split on HTML table markers
                                const parts = content.split(/<!--HTML_TABLE-->|<!--\/HTML_TABLE-->/);
                                return parts.map((part, idx) => {
                                  const trimmed = part.trim();
                                  if (!trimmed) return null;
                                  // Even indices are markdown, odd indices are HTML tables
                                  if (idx % 2 === 1) {
                                    // Raw HTML table
                                    return (
                                      <div key={idx} className="overflow-x-auto my-4 sec-table-wrapper">
                                        <div dangerouslySetInnerHTML={{ __html: trimmed }} />
                                      </div>
                                    );
                                  }
                                  // Markdown content
                                  return (
                                    <ReactMarkdown
                                      key={idx}
                                      remarkPlugins={[remarkGfm]}
                                      components={{
                                        h2: ({ children }) => <h3 className="text-blue-400 font-semibold text-base mt-6 mb-2">{children}</h3>,
                                        h3: ({ children }) => <h4 className="text-blue-300 font-semibold text-sm mt-5 mb-2">{children}</h4>,
                                        p: ({ children }) => <p className="mb-3">{children}</p>,
                                        ul: ({ children }) => <ul className="list-disc list-outside pl-6 mb-3 space-y-1">{children}</ul>,
                                        ol: ({ children }) => <ol className="list-decimal list-outside pl-6 mb-3 space-y-1">{children}</ol>,
                                        li: ({ children }) => <li>{children}</li>,
                                        strong: ({ children }) => <strong className="text-gray-100 font-semibold">{children}</strong>,
                                      }}
                                    >
                                      {trimmed}
                                    </ReactMarkdown>
                                  );
                                });
                              })()}
                            </div>
                          )}
                          {!mdnaLoading && !mdnaError && !mdnaText && !mdnaMarkdown && (
                            <p className="text-gray-500 text-sm italic py-4 text-center">Select a company to view the Management Discussion & Analysis.</p>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* ── In The News Tab Content ───────────────────── */}
              {mainTab === "news" && selectedCompany && (
                <div className="flex gap-4">

                  {/* LEFT: Main News Feed (50%) */}
                  <div style={{ width: '50%' }}>
                    <div style={{ height: 'calc(100vh - 220px)', overflowY: 'auto' }} className="pr-2">
                      <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-widest mb-3">News</h3>
                      {newsLoading && (
                        <div className="flex items-center gap-3 py-8">
                          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-400"></div>
                          <span className="text-gray-400 text-sm">Fetching latest news...</span>
                        </div>
                      )}
                      {newsError && (
                        <div className="bg-red-900/30 border border-red-700/50 rounded-md p-3 text-sm text-red-300 mb-3">
                          {newsError}
                        </div>
                      )}
                      {!newsLoading && !newsError && newsData.length === 0 && (
                        <p className="text-gray-500 text-sm italic py-4">No news found for {selectedCompany.ticker}.</p>
                      )}
                      {!newsLoading && newsData.map((item, idx) => (
                        <div
                          key={idx}
                          onClick={() => openNewsSummary(item)}
                          className="bg-gray-800 border border-gray-700 rounded-xl p-4 mb-3 hover:border-blue-500/50 transition-colors cursor-pointer"
                        >
                          <div className="flex items-center gap-3 text-xs mb-2">
                            {item.source && (
                              <span className="flex items-center gap-2">
                                <img
                                  src={`https://www.google.com/s2/favicons?domain=${item.domain || (item.link ? new URL(item.link).hostname : '')}&sz=32`}
                                  alt=""
                                  className="w-5 h-5 rounded flex-shrink-0"
                                  onError={(e) => { e.target.style.display = 'none'; }}
                                />
                                <span className="text-blue-400 font-medium">{item.source}</span>
                              </span>
                            )}
                            {item.source && item.relative_date && (
                              <span className="text-gray-700">·</span>
                            )}
                            {item.relative_date && (
                              <span className="text-gray-600">{item.relative_date}</span>
                            )}
                          </div>
                          <div className="text-white hover:text-gray-200 font-semibold text-sm block mb-2 transition-colors truncate">
                            {item.title}
                          </div>
                          {item.snippet && (
                            <p className="text-blue-100 text-xs leading-relaxed">{item.snippet}</p>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* RIGHT: Social Buzz (50%) */}
                  <div style={{ width: '50%' }}>
                    <div style={{ height: 'calc(100vh - 220px)', overflowY: 'auto' }} className="pl-2">
                      <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-widest mb-3">Social Buzz</h3>

                      {socialLoading && (
                        <div className="flex items-center gap-3 py-4">
                          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-purple-400"></div>
                          <span className="text-gray-400 text-sm">Fetching social feeds...</span>
                        </div>
                      )}

                      {/* All platforms — flat list, no per-platform header */}
                      {['reddit', 'substack', 'x'].map(key => (
                        socialData[key].map((item, idx) => (
                          <div
                            key={`${key}-${idx}`}
                            onClick={() => openNewsSummary(item)}
                            className="bg-gray-800 border border-gray-700 rounded-xl p-4 mb-3 hover:border-blue-500/50 transition-colors cursor-pointer"
                          >
                            <div className="flex items-center gap-3 text-xs mb-2">
                              {item.source && (
                                <span className="flex items-center gap-2">
                                  <img
                                    src={`https://www.google.com/s2/favicons?domain=${item.domain || (item.link ? new URL(item.link).hostname : '')}&sz=32`}
                                    alt=""
                                    className="w-5 h-5 rounded flex-shrink-0"
                                    onError={(e) => { e.target.style.display = 'none'; }}
                                  />
                                  <span className="text-blue-400 font-medium">{item.source}</span>
                                </span>
                              )}
                              {item.source && item.relative_date && <span className="text-gray-700">·</span>}
                              {item.relative_date && <span className="text-gray-600">{item.relative_date}</span>}
                            </div>
                            <div className="text-white hover:text-gray-200 font-semibold text-sm block mb-2 transition-colors truncate">
                              {item.title}
                            </div>
                            {item.snippet && (
                              <p className="text-blue-100 text-xs leading-relaxed">{item.snippet}</p>
                            )}
                          </div>
                        ))
                      ))}
                      {!socialLoading && ['reddit', 'substack', 'x'].every(k => socialData[k].length === 0) && (
                        <p className="text-gray-500 text-sm italic py-4">No social results found.</p>
                      )}
                    </div>
                  </div>

                </div>
              )}

            </div>
          </div>
        </div>


        {/* ── Scatter Chart Expand Modal ─────────────────────────────────────── */}
        {scatterModal && ReactDOM.createPortal(
          <div
            style={{ position: 'fixed', inset: 0, zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: 'rgba(0,0,0,0.85)' }}
            onClick={() => setScatterModal(null)}
          >
            <div
              style={{ position: 'relative', width: '80vw', height: '80vh', backgroundColor: '#0f172a', borderRadius: '12px', border: '1px solid #1e293b', boxShadow: '0 25px 60px rgba(0,0,0,0.7)', display: 'flex', flexDirection: 'column', padding: '20px' }}
              onClick={e => e.stopPropagation()}
            >
              {/* Header */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
                <span style={{ color: '#e2e8f0', fontSize: '16px', fontWeight: '600', letterSpacing: '0.05em' }}>{scatterModal.title}</span>
                <button
                  onClick={() => setScatterModal(null)}
                  style={{ color: '#94a3b8', background: 'none', border: 'none', cursor: 'pointer', fontSize: '18px', lineHeight: 1, padding: '4px 8px', borderRadius: '6px' }}
                  onMouseEnter={e => e.currentTarget.style.color = '#e2e8f0'}
                  onMouseLeave={e => e.currentTarget.style.color = '#94a3b8'}
                  title="Close"
                >✕</button>
              </div>
              {/* Chart fills remaining space — dataZoom enabled for scroll/pinch zoom */}
              <div style={{ flex: 1, minHeight: 0 }}>
                <ReactECharts
                  option={{
                    ...scatterModal.option,
                    grid: { ...((scatterModal.option.grid || {})), bottom: 60, left: 80, top: 36, right: 20 },
                    legend: {
                      ...(scatterModal.option.legend || {}),
                      textStyle: { ...((scatterModal.option.legend || {}).textStyle || {}), fontSize: 15 },
                    },
                    series: (scatterModal.option.series || []).map(s => ({
                      ...s,
                      label: s.label ? { ...s.label, fontSize: Math.min((s.label.fontSize || 8) + 3, 13) } : undefined,
                    })),
                    xAxis: {
                      ...(scatterModal.option.xAxis || {}),
                      nameGap: 36,
                      nameTextStyle: { ...((scatterModal.option.xAxis || {}).nameTextStyle || {}), color: '#f1f5f9', fontSize: 14, fontWeight: '600' },
                      axisLabel: { ...((scatterModal.option.xAxis || {}).axisLabel || {}), color: '#e2e8f0', fontSize: 13 },
                    },
                    yAxis: {
                      ...(scatterModal.option.yAxis || {}),
                      nameGap: 62,
                      nameTextStyle: { ...((scatterModal.option.yAxis || {}).nameTextStyle || {}), color: '#f1f5f9', fontSize: 14, fontWeight: '600' },
                      axisLabel: { ...((scatterModal.option.yAxis || {}).axisLabel || {}), color: '#e2e8f0', fontSize: 13 },
                    },
                    tooltip: {
                      ...(scatterModal.option.tooltip || {}),
                      axisPointer: {
                        type: 'cross',
                        lineStyle: { type: 'dashed', color: 'rgba(255,255,255,0.35)', width: 1 },
                        crossStyle: { type: 'dashed', color: 'rgba(255,255,255,0.35)', width: 1 },
                        label: { show: false },
                      },
                    },
                    dataZoom: [
                      { type: 'inside', xAxisIndex: 0, start: 0, end: 100, minSpan: 5, zoomOnMouseWheel: true, moveOnMouseMove: true },
                      { type: 'inside', yAxisIndex: 0, start: 0, end: 100, minSpan: 5, zoomOnMouseWheel: true, moveOnMouseMove: true },
                    ],
                  }}
                  style={{ height: '100%', width: '100%' }}
                  opts={{ renderer: 'canvas' }}
                />
              </div>
            </div>
          </div>,
          document.body
        )}

        {/* ── News AI-Summary Modal ───────────────────────────────────────── */}
        {newsModal && ReactDOM.createPortal(
          <div
            style={{ position: 'fixed', inset: 0, zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: 'rgba(0,0,0,0.85)', padding: '24px' }}
            onClick={() => setNewsModal(null)}
          >
            <div
              style={{ position: 'relative', width: '720px', maxWidth: '92vw', maxHeight: '85vh', backgroundColor: '#0f172a', borderRadius: '12px', border: '1px solid #1e293b', boxShadow: '0 25px 60px rgba(0,0,0,0.7)', display: 'flex', flexDirection: 'column' }}
              onClick={e => e.stopPropagation()}
            >
              {/* Header */}
              <div className="flex items-start justify-between gap-4 px-5 pt-5 pb-3 border-b border-gray-800">
                <div className="min-w-0 flex-1">
                  {newsModal.source && (
                    <div className="flex items-center gap-2 mb-1">
                      <img
                        src={`https://www.google.com/s2/favicons?domain=${(() => { try { return new URL(newsModal.url).hostname; } catch { return ''; } })()}&sz=32`}
                        alt=""
                        className="w-4 h-4 rounded flex-shrink-0"
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                      <span className="text-blue-400 text-xs font-medium">{newsModal.source}</span>
                    </div>
                  )}
                  <h3 className="text-white text-base font-semibold leading-snug">
                    {newsModal.title || 'News article'}
                  </h3>
                </div>
                <button
                  onClick={() => setNewsModal(null)}
                  className="text-gray-400 hover:text-gray-100 text-lg leading-none px-2 py-1 rounded"
                  title="Close"
                >✕</button>
              </div>

              {/* Body */}
              <div className="px-5 py-4 overflow-y-auto" style={{ flex: 1 }}>
                {newsModal.loading && (
                  <div className="flex items-center gap-3 py-6">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-400"></div>
                    <span className="text-gray-400 text-sm">Summarizing article…</span>
                  </div>
                )}

                {!newsModal.loading && newsModal.summary && (
                  <div className="text-gray-200 text-sm leading-relaxed whitespace-pre-wrap">
                    {newsModal.summary}
                  </div>
                )}

                {!newsModal.loading && !newsModal.summary && (
                  <div className="space-y-3">
                    {newsModal.snippet && (
                      <p className="text-gray-300 text-sm leading-relaxed">{newsModal.snippet}</p>
                    )}
                    <div className="bg-amber-900/20 border border-amber-700/40 rounded-md p-3 text-xs text-amber-200">
                      {newsModal.error || "Couldn't generate a summary for this article."} Use the link below to read it at the source.
                    </div>
                  </div>
                )}
              </div>

              {/* Footer */}
              <div className="flex items-center justify-between px-5 py-3 border-t border-gray-800 text-xs">
                <span className="text-gray-500">AI summary · Gemini 2.5 Flash</span>
                <a
                  href={newsModal.url}
                  target="_blank"
                  rel="noreferrer"
                  className="text-blue-400 hover:text-blue-300 font-medium inline-flex items-center gap-1"
                >
                  Read original ↗
                </a>
              </div>
            </div>
          </div>,
          document.body
        )}

        {/* Right Panel - Tabs (disabled for now)
        <div className="w-84 flex-shrink-0 border-l border-white/10 hidden lg:flex flex-col" style={{ width: '330px', height: 'calc(100vh - 66px)' }}>
          <RightTabPanel />
        </div>
        */}

        {/* Auth CTA Modal */}
        <AuthCtaModal
          isOpen={isAuthCtaOpen}
          onClose={() => setIsAuthCtaOpen(false)}
        />
      </div>
    );
  }

  // Show error content only if there's an error
  if (hasError) {
    return (
      <>
        <div className="min-h-screen bg-gray-900 text-gray-100 p-8">
          <div className="max-w-4xl mx-auto">
            <h1 className="text-3xl font-bold mb-6">Stock View - BigQuery Connection Test</h1>

            {error && (
              <div className="bg-red-900 border border-red-700 rounded-lg p-4 mb-4">
                <h3 className="text-lg font-semibold mb-2 text-red-200">Connection Error</h3>
                <p className="text-red-100">{error}</p>
              </div>
            )}

            {result && (result.error || !result.success) && (
              <div className="bg-gray-800 border border-gray-700 rounded-lg p-6">
                <h2 className="text-2xl font-bold mb-4 text-red-400">BigQuery Connection Failed</h2>

                <div className="space-y-4">
                  {result.error && (
                    <div className="bg-red-900 border border-red-700 rounded p-3">
                      <span className="font-semibold text-red-200">Error: </span>
                      <span className="text-red-100">{result.error}</span>
                    </div>
                  )}

                  {result.output && (
                    <div className="mt-4">
                      <h3 className="text-lg font-semibold mb-2 text-gray-300">
                        Output Log:
                      </h3>
                      <pre className="bg-gray-900 border border-gray-700 rounded p-4 overflow-x-auto text-sm text-gray-300 font-mono whitespace-pre-wrap">
                        {result.output}
                      </pre>
                    </div>
                  )}
                </div>
              </div>
            )}


          </div>
        </div>
      </>
    );
  }

  // Loading state (blank while testing)
  if (!result && loading) {
    return null;
  }

  return null;
}

